import {
    distanceToRect,
    hashString,
    lerpHexColor,
    round2,
    roundVec2,
} from "./colors";
import {
    EPS,
    mirrorPointSets,
    sameVec,
} from "./symmetry";
import type {
    SymmetryState,
} from "./types";
import {
    resolveConnectionStyle,
} from "./factories";
import type {
    CircleSpawnPattern,
    ConcentricRingsEffect,
    Connection,
    ConnectionObjectOrder,
    ConnectionPreset,
    ConnectionRouting,
    ConnectSelectionOptions,
    CornerStyle,
    DiagramArc,
    DiagramDocument,
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLine,
    DiagramNode,
    DiagramPolygon,
    DiagramRect,
    InteractFalloff,
    NodeInteractOptions,
    NodeShape,
    OutlineProperty,
    RadialEmitterEffect,
    ResolvedConnectionStyle,
    SpawnPattern,
    SquareSpawnPattern,
    StrokeStyle,
    Vec2,
} from "./types";
import {
    newId,
} from "./types";

/**
 * The node-id pairs the Connect-selection subtool should link, given the chosen
 * `selectedIds` (in selection order) and `options`. Pure — resolves each id to
 * its node's center, applies the proximity thresholds, then enumerates pairs per
 * `options.mode`:
 * - `complete`: every eligible pair (with a max threshold, a proximity graph).
 * - `sequence`: consecutive selected nodes, dropping links that fail the filter.
 * - `nearest`: each node to its single closest eligible neighbor (deduped, so a
 *   mutual nearest pair is emitted once).
 *
 * Returned pairs are unordered and de-duplicated; the caller still funnels each
 * through `buildConnectionAdditions`, which enforces same-layer / no-duplicate /
 * symmetry rules. Ids without a matching node are ignored.
 */
export function connectionPairsForSelection(
    nodes: DiagramNode[],
    selectedIds: string[],
    options: ConnectSelectionOptions,
): Array<[string, string]> {
    const byId = new Map(nodes.map((n) => [n.id, n] as const));
    const ids = selectedIds.filter((id) => byId.has(id));
    if (ids.length < 2) return [];

    const minD = options.minDistanceEnabled ? Math.max(0, options.minDistance) : 0;
    const maxD = options.maxDistanceEnabled
        ? Math.max(0, options.maxDistance)
        : Infinity;
    const distOf = (a: string, b: string): number => {
        const pa = byId.get(a)!.position;
        const pb = byId.get(b)!.position;
        return Math.hypot(pa.x - pb.x, pa.y - pb.y);
    };
    const passes = (d: number): boolean => d >= minD && d <= maxD;

    const out: Array<[string, string]> = [];
    const seen = new Set<string>();
    const key = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
    const add = (a: string, b: string) => {
        if (a === b) return;
        const k = key(a, b);
        if (seen.has(k)) return;
        seen.add(k);
        out.push([a, b]);
    };

    if (options.mode === "sequence") {
        for (let i = 0; i + 1 < ids.length; i += 1) {
            const a = ids[i]!;
            const b = ids[i + 1]!;
            if (passes(distOf(a, b))) add(a, b);
        }
        return out;
    }

    if (options.mode === "nearest") {
        for (const a of ids) {
            let best: string | null = null;
            let bestD = Infinity;
            for (const b of ids) {
                if (b === a) continue;
                const d = distOf(a, b);
                if (passes(d) && d < bestD) {
                    bestD = d;
                    best = b;
                }
            }
            if (best) add(a, best);
        }
        return out;
    }

    // complete: every eligible pair.
    for (let i = 0; i < ids.length; i += 1) {
        for (let j = i + 1; j < ids.length; j += 1) {
            const a = ids[i]!;
            const b = ids[j]!;
            if (passes(distOf(a, b))) add(a, b);
        }
    }
    return out;
}

// --- Node-interact subtool (attract / repel) -----------------------------
// One "pulse" of attraction/repulsion dispatched on a node: nearby nodes move
// a fraction of the way toward (attract) or away from (repel) it, the fraction
// scaled by a distance falloff. Pure so the math is isolated and testable; the
// context owns the React/document dispatch (snapshot + `moveNodes`).

/**
 * The 0..1 influence weight for normalized distance `t` (0 at the dispatched
 * node, 1 at the influence-radius edge) under each {@link InteractFalloff}.
 * Always full (1) at the center and 0 at the edge; `t` is clamped to 0..1.
 */
export function interactFalloffWeight(
    t: number,
    falloff: InteractFalloff,
): number {
    const x = t < 0 ? 0 : t > 1 ? 1 : t;
    switch (falloff) {
        case "constant":
            return 1;
        case "linear":
            return 1 - x;
        case "ease-in":
            // Concentrated near the center, falling off fast.
            return (1 - x) * (1 - x);
        case "ease-out":
            // Broad plateau near the center, dropping near the edge.
            return 1 - x * x;
        case "smooth":
        default: {
            // Smoothstep on the remaining distance (gentle at center and edge).
            const s = 1 - x;
            return s * s * (3 - 2 * s);
        }
    }
}

/**
 * Plan the moves one node-interact pulse applies. For each candidate node within
 * `options.radius` of `host`, move it a fraction `w = falloff(t) · strength`
 * (0..1) of the way toward `host` (`attract`) or toward the point at the radius
 * edge directly outward from `host` through the node (`repel`). The host itself
 * and anything at its exact position are skipped (handled by the distance test);
 * callers should also exclude the host and any nodes that shouldn't move (spawn
 * clones, locked layers). Returns rounded id→position moves; pure given its args.
 */
export function nodeInteractionMoves(
    host: Vec2,
    candidates: ReadonlyArray<{ id: string; position: Vec2 }>,
    options: NodeInteractOptions,
): Array<{ id: string; pos: Vec2 }> {
    const R = Math.max(0, options.radius);
    const strength = Math.max(0, Math.min(1, options.strength));
    if (R <= 0 || strength <= 0) return [];
    const attract = options.action === "attract";
    const moves: Array<{ id: string; pos: Vec2 }> = [];
    for (const n of candidates) {
        const dx = n.position.x - host.x;
        const dy = n.position.y - host.y;
        const d = Math.hypot(dx, dy);
        if (d <= EPS || d > R) continue;
        const w = interactFalloffWeight(d / R, options.falloff) * strength;
        if (w <= 0) continue;
        let target: Vec2;
        if (attract) {
            target = host;
        } else {
            // Repel toward the influence edge, straight out through the node.
            target = { x: host.x + (dx / d) * R, y: host.y + (dy / d) * R };
        }
        moves.push({
            id: n.id,
            pos: roundVec2({
                x: n.position.x + (target.x - n.position.x) * w,
                y: n.position.y + (target.y - n.position.y) * w,
            }),
        });
    }
    return moves;
}

/**
 * The node-id pairs a `connect` node-interact pulse dispatched on `host` should
 * link, given the eligible `candidates` (callers exclude the host itself, spawn
 * clones, and locked-layer nodes). Only candidates within `options.radius` of
 * the host participate. Pure: the caller still funnels each pair through the
 * context's connection rules (same layer, no duplicates, symmetry, active
 * preset), so this just decides the topology.
 *
 * - `direct`: a hub — the host links straight to every in-range candidate.
 * - `bridge`: a tree grown from the host (Prim's minimum spanning tree over the
 *   host + in-range candidates by Euclidean distance), so each node joins via
 *   its nearest already-connected node. Far nodes are reached through the nodes
 *   between them and the host rather than by one long direct edge.
 */
export function nodeConnectPulsePairs(
    host: { id: string; position: Vec2 },
    candidates: ReadonlyArray<{ id: string; position: Vec2 }>,
    options: NodeInteractOptions,
): Array<{ a: string; b: string }> {
    const R = Math.max(0, options.radius);
    if (R <= 0) return [];
    const inRange = candidates.filter(
        (c) =>
            Math.hypot(
                c.position.x - host.position.x,
                c.position.y - host.position.y,
            ) <= R,
    );
    if (inRange.length === 0) return [];

    if (options.connectMode === "direct") {
        return inRange.map((c) => ({ a: host.id, b: c.id }));
    }

    // Bridge: grow a tree from the host. Each step adds the remaining node whose
    // nearest already-in-tree node is closest, linked to that node — so distant
    // nodes route through the in-between nodes instead of a single long edge.
    const pairs: Array<{ a: string; b: string }> = [];
    const tree: Array<{ id: string; position: Vec2 }> = [host];
    const remaining = [...inRange];
    while (remaining.length > 0) {
        let bestIdx = -1;
        let bestParent = "";
        let bestD = Infinity;
        for (let i = 0; i < remaining.length; i += 1) {
            const node = remaining[i]!;
            for (const t of tree) {
                const d = Math.hypot(
                    node.position.x - t.position.x,
                    node.position.y - t.position.y,
                );
                if (d < bestD) {
                    bestD = d;
                    bestIdx = i;
                    bestParent = t.id;
                }
            }
        }
        if (bestIdx < 0) break;
        const node = remaining[bestIdx]!;
        pairs.push({ a: bestParent, b: node.id });
        tree.push(node);
        remaining.splice(bestIdx, 1);
    }
    return pairs;
}

// --- Interaction transforms (scale / rotate / mirror) --------------------
// Pure point transforms the canvas Scale and Rotate subtools apply to each
// dragged element's defining points, plus the shared "reflect a moved/rotated
// two-point shape onto its existing mirror copies" helper. Kept here (rather
// than inline in the viewer) so the math is isolated and testable; the viewer
// still owns the React/context dispatch that feeds movers in and pushes the
// results to `moveNodes`/`moveEllipses`/etc.

/**
 * Scale a point's offset from `anchor` by independent per-axis factors. The
 * Scale subtool drives `fx`/`fy` from the pointer's per-axis travel away from
 * the anchor (Illustrator-style), so X and Y can stretch independently.
 */
export function scalePointAbout(
    p: Vec2,
    anchor: Vec2,
    fx: number,
    fy: number,
): Vec2 {
    return {
        x: anchor.x + (p.x - anchor.x) * fx,
        y: anchor.y + (p.y - anchor.y) * fy,
    };
}

/** Rotate a point's offset from `pivot` by `rad` radians (clockwise; SVG +y down). */
export function rotatePointAbout(p: Vec2, pivot: Vec2, rad: number): Vec2 {
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = p.x - pivot.x;
    const dy = p.y - pivot.y;
    return {
        x: pivot.x + dx * cos - dy * sin,
        y: pivot.y + dx * sin + dy * cos,
    };
}

/**
 * Reflect a moved/rotated two-point shape (`movedStart`/`movedEnd`) onto its
 * existing mirror copies. `mirrorIds[i]` names the copy paired with the i-th
 * `mirrorPointSets` reflection (captured at drag/rotate start), or "" when no
 * copy was found for that mirror — those are skipped. Used by the Select-drag,
 * Scale, and Rotate flows so two-point shapes keep their counterparts as true
 * reflections under active symmetry.
 */
export function mirroredTwoPointUpdates(
    movedStart: Vec2,
    movedEnd: Vec2,
    mirrorIds: ReadonlyArray<string> | undefined,
    sym: SymmetryState,
    center: Vec2,
): Array<{ id: string; start: Vec2; end: Vec2 }> {
    if (!mirrorIds || mirrorIds.length === 0) return [];
    const sets = mirrorPointSets([movedStart, movedEnd], sym, center);
    const out: Array<{ id: string; start: Vec2; end: Vec2 }> = [];
    for (let i = 0; i < mirrorIds.length; i += 1) {
        const id = mirrorIds[i];
        const set = sets[i];
        if (!id || !set) continue;
        out.push({ id, start: set.points[0]!, end: set.points[1]! });
    }
    return out;
}

// --- Heat math (proximity-driven recoloring) -----------------------------
// Shared by the live viewer and the string exporter so heat-reactive
// elements recolor identically in both. All pure over (point, zones).

/**
 * The strongest heat zone influencing `point`, as `{ color, intensity }`, or
 * null when no zone reaches it. Intensity is 1 inside a zone's rectangle and
 * ramps linearly to 0 at `radius` units beyond its edge. When zones overlap,
 * the one with the highest intensity at this point wins.
 */
export function heatAt(
    point: Vec2,
    zones: ReadonlyArray<DiagramHeatZone>,
): { color: string; intensity: number } | null {
    let best: { color: string; intensity: number } | null = null;
    for (const z of zones) {
        const min = { x: Math.min(z.start.x, z.end.x), y: Math.min(z.start.y, z.end.y) };
        const max = { x: Math.max(z.start.x, z.end.x), y: Math.max(z.start.y, z.end.y) };
        const dist = distanceToRect(point, min, max);
        const radius = Math.max(0, z.radius);
        const intensity =
            dist <= 0
                ? 1
                : radius <= 0
                  ? 0
                  : Math.max(0, 1 - dist / radius);
        if (intensity <= 0) continue;
        if (!best || intensity > best.intensity) {
            best = { color: z.color, intensity };
        }
    }
    return best;
}

/**
 * Blend `baseColor` toward the nearest influencing heat zone's color by its
 * intensity at `point`. Returns `baseColor` unchanged when no zone reaches the
 * point (or when zones is empty), so non-reactive output is untouched.
 */
export function applyHeatColor(
    baseColor: string,
    point: Vec2,
    zones: ReadonlyArray<DiagramHeatZone>,
): string {
    if (zones.length === 0) return baseColor;
    const heat = heatAt(point, zones);
    if (!heat) return baseColor;
    return lerpHexColor(baseColor, heat.color, heat.intensity);
}

// --- spawn pattern geometry --------------------------------------------------

/**
 * Positions for a square ring of clones around `center`. `countPerSide`
 * clones run along each side of a square whose half-extent is derived from
 * the template's radius plus `gap` per slot, so adjacent clones (and the
 * ring's corners) sit `gap` apart edge-to-edge. Any slot that lands on
 * `center` itself is omitted.
 */
function squareSpawnPositions(
    center: Vec2,
    pattern: Pick<SquareSpawnPattern, "countPerSide" | "gap">,
    templateRadius: number,
): Vec2[] {
    const perSide = Math.max(1, Math.floor(pattern.countPerSide));
    const step = Math.max(0, templateRadius * 2 + pattern.gap);
    // Side length spans `perSide` clone slots edge-to-edge; half-extent is
    // measured from center to the row/column of slot centers.
    const half = (step * (perSide - 1)) / 2;
    if (half < EPS) {
        // A 1-per-side ring collapses to a single point at the center; there
        // is nothing to ring around, so skip it rather than stacking on A.
        return [];
    }

    const slots: Vec2[] = [];
    for (let i = 0; i < perSide; i += 1) {
        const offset = -half + step * i;
        // Top and bottom edges (excluding corners already covered by the
        // left/right edges below would double-count corners, so each edge
        // includes both its corners; duplicates are filtered after).
        slots.push({ x: center.x + offset, y: center.y - half }); // top
        slots.push({ x: center.x + offset, y: center.y + half }); // bottom
        slots.push({ x: center.x - half, y: center.y + offset }); // left
        slots.push({ x: center.x + half, y: center.y + offset }); // right
    }

    const out: Vec2[] = [];
    for (const s of slots) {
        const rounded = roundVec2(s);
        if (sameVec(rounded, center)) continue;
        if (out.some((o) => sameVec(o, rounded))) continue;
        out.push(rounded);
    }
    return out;
}

/** Positions for a circular ring of `count` clones at `radius` around `center`. */
function circleSpawnPositions(
    center: Vec2,
    pattern: Pick<CircleSpawnPattern, "count" | "radius">,
): Vec2[] {
    const count = Math.max(1, Math.floor(pattern.count));
    if (pattern.radius < EPS) return [];
    const out: Vec2[] = [];
    for (let i = 0; i < count; i += 1) {
        const angle = (i / count) * Math.PI * 2;
        const pos = roundVec2({
            x: center.x + Math.cos(angle) * pattern.radius,
            y: center.y + Math.sin(angle) * pattern.radius,
        });
        if (sameVec(pos, center)) continue;
        out.push(pos);
    }
    return out;
}

/**
 * Clone positions for a spawn pattern around `center` (the host node).
 * `templateRadius` is only used by the square pattern, to size the ring so
 * clones don't overlap based on the template's own marker size.
 */
export function spawnPositions(
    center: Vec2,
    pattern: SpawnPattern,
    templateRadius: number,
): Vec2[] {
    return pattern.kind === "circle"
        ? circleSpawnPositions(center, pattern)
        : squareSpawnPositions(center, pattern, templateRadius);
}

/**
 * Rebuild the clone nodes spawned by `hostId`'s pattern: removes its
 * existing clones and, if the host still has a `spawn` pattern with a live
 * template, creates fresh ones at the pattern's current positions with the
 * template's current appearance. No-op (clones removed only) when the host
 * has no pattern or its template no longer exists. `existingIds` is consulted
 * so document ids stay free of accidental collisions, but new clone ids are
 * always freshly minted — clones are pure derived state, never user-authored.
 */
export function regenerateNodeSpawn(
    nodes: DiagramNode[],
    hostId: string,
): DiagramNode[] {
    const host = nodes.find((n) => n.id === hostId);
    const withoutOldClones = nodes.filter(
        (n) => !(n.spawnedFrom && n.spawnedFrom.hostId === hostId),
    );
    if (!host || !host.spawn) return withoutOldClones;

    const template = nodes.find((n) => n.id === host.spawn!.templateId);
    if (!template) return withoutOldClones;

    const positions = spawnPositions(
        host.position,
        host.spawn,
        template.radius,
    );
    const clones: DiagramNode[] = positions.map((pos, i) => ({
        id: newId("node"),
        label: `${template.label} (spawn ${i + 1})`,
        // Clones live on the host's layer (the pattern is part of the host).
        layerId: host.layerId,
        position: pos,
        radius: template.radius,
        color: template.color,
        shape: template.shape,
        visible: template.visible,
        effectIds: [...template.effectIds],
        spawn: null,
        spawnedFrom: { hostId, templateId: template.id },
    }));
    return [...withoutOldClones, ...clones];
}

// --- property geometry ------------------------------------------------------

/**
 * Ray segments for a radial-emitter property: `rayCount` lines spaced evenly,
 * each running from the inner ring to the outer ring around `center`.
 */
export function radialEmitterSegments(
    center: Vec2,
    prop: Pick<RadialEmitterEffect, "rayCount" | "innerRadius" | "outerRadius">,
): Array<{ from: Vec2; to: Vec2 }> {
    const count = Math.max(1, Math.floor(prop.rayCount));
    const segments: Array<{ from: Vec2; to: Vec2 }> = [];
    for (let i = 0; i < count; i += 1) {
        const angle = (i / count) * Math.PI * 2;
        const dx = Math.cos(angle);
        const dy = Math.sin(angle);
        segments.push({
            from: {
                x: center.x + dx * prop.innerRadius,
                y: center.y + dy * prop.innerRadius,
            },
            to: {
                x: center.x + dx * prop.outerRadius,
                y: center.y + dy * prop.outerRadius,
            },
        });
    }
    return segments;
}

/**
 * Radii for a concentric-rings effect: `ringCount` circles starting at
 * `innerRadius` and stepping out by `spacing`. Non-positive radii are dropped
 * so an inner radius of 0 doesn't draw a degenerate ring at the node center.
 */
export function concentricRingRadii(
    prop: Pick<ConcentricRingsEffect, "ringCount" | "innerRadius" | "spacing">,
): number[] {
    const count = Math.max(1, Math.floor(prop.ringCount));
    const radii: number[] = [];
    for (let i = 0; i < count; i += 1) {
        const r = prop.innerRadius + prop.spacing * i;
        if (r > 0) radii.push(round2(r));
    }
    return radii;
}

// --- connection geometry ----------------------------------------------------
//
// Connection rendering is two composable layers so that line *shape*
// (straight today; wavy / 90-degree / rounded later) and parallel-line
// *multiplicity* stay orthogonal:
//   1. connectionCenterline() turns the two endpoints into an ordered
//      polyline. This is the only place future line shapes plug in.
//   2. offsetPolyline() shifts any polyline perpendicular to its local
//      direction, so parallel offsetting works for every shape for free.
// connectionPaths() composes them into one `d` string per parallel line.

/**
 * Build an orthogonal (90-degree) staircase between `a` and `b` with `corners`
 * bends. The path is split into `corners + 1` legs that alternate between
 * horizontal and vertical, leaving `a` along the dominant axis. Each set of
 * same-direction legs evenly divides that axis's total travel.
 */
function orthogonalCenterline(a: Vec2, b: Vec2, corners: number): Vec2[] {
    const k = Math.max(1, Math.floor(corners));
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) return [a, b];

    const horizontalFirst = Math.abs(dx) >= Math.abs(dy);
    const legs = k + 1;
    // Count how many legs move along each axis so we can split travel evenly.
    let hLegs = 0;
    let vLegs = 0;
    for (let i = 0; i < legs; i += 1) {
        const horizontal = horizontalFirst ? i % 2 === 0 : i % 2 === 1;
        if (horizontal) hLegs += 1;
        else vLegs += 1;
    }
    const hStep = hLegs > 0 ? dx / hLegs : 0;
    const vStep = vLegs > 0 ? dy / vLegs : 0;

    const points: Vec2[] = [a];
    let cur = { ...a };
    for (let i = 0; i < legs; i += 1) {
        const horizontal = horizontalFirst ? i % 2 === 0 : i % 2 === 1;
        cur = horizontal
            ? { x: cur.x + hStep, y: cur.y }
            : { x: cur.x, y: cur.y + vStep };
        points.push(cur);
    }
    // The final accumulated point should equal `b`; snap to remove float drift.
    points[points.length - 1] = { ...b };
    return points;
}

/**
 * Build a 45-degree staircase between `a` and `b` with `corners` bends.
 * Same leg layout as the orthogonal case, but each "cross" leg travels at 45
 * degrees (equal x and y) while the dominant-axis legs take up the slack so the
 * path still lands exactly on `b`.
 */
function diagonalCenterline(a: Vec2, b: Vec2, corners: number): Vec2[] {
    const k = Math.max(1, Math.floor(corners));
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) return [a, b];

    const horizontalFirst = Math.abs(dx) >= Math.abs(dy);
    const legs = k + 1;
    // Diagonal legs are the "cross" (non-dominant-first) legs; they consume an
    // equal amount of the minor axis and an equal amount of the major axis.
    const diagIsHorizontalLeg = !horizontalFirst;
    let diagLegs = 0;
    let majorLegs = 0;
    for (let i = 0; i < legs; i += 1) {
        const horizontal = horizontalFirst ? i % 2 === 0 : i % 2 === 1;
        if (horizontal === diagIsHorizontalLeg) diagLegs += 1;
        else majorLegs += 1;
    }
    // Each diagonal leg covers the same signed minor-axis amount; its major-axis
    // travel equals that magnitude (45 degrees). Straight legs absorb the rest.
    const minor = horizontalFirst ? dy : dx;
    const major = horizontalFirst ? dx : dy;
    const diagMinorStep = diagLegs > 0 ? minor / diagLegs : 0;
    const diagMajorStep = Math.sign(major) * Math.abs(diagMinorStep);
    const majorRemaining = major - diagMajorStep * diagLegs;
    const majorStep = majorLegs > 0 ? majorRemaining / majorLegs : 0;

    const points: Vec2[] = [a];
    let cur = { ...a };
    for (let i = 0; i < legs; i += 1) {
        const horizontal = horizontalFirst ? i % 2 === 0 : i % 2 === 1;
        const isDiag = horizontal === diagIsHorizontalLeg;
        if (isDiag) {
            // 45-degree leg: advance major + minor together.
            cur = horizontalFirst
                ? { x: cur.x + diagMajorStep, y: cur.y + diagMinorStep }
                : { x: cur.x + diagMinorStep, y: cur.y + diagMajorStep };
        } else {
            // Straight leg along the major axis.
            cur = horizontalFirst
                ? { x: cur.x + majorStep, y: cur.y }
                : { x: cur.x, y: cur.y + majorStep };
        }
        points.push(cur);
    }
    points[points.length - 1] = { ...b };
    return points;
}

/**
 * Build a sine wave between `a` and `b`, sampled into a dense polyline. The wave
 * runs along the chord (a→b) and displaces perpendicular to it by
 * `amplitude * sin(2π · distance / wavelength)`, so it swings to both sides of
 * the straight line. Both endpoints sit exactly on the nodes (the sine is zero
 * there). Sampling is fixed per-wavelength so the curve stays smooth regardless
 * of chord length; offsetting and object population compose on the result.
 */
function sineSegment(
    a: Vec2,
    b: Vec2,
    amplitude: number,
    wavelength: number,
    phase0 = 0,
): { points: Vec2[]; endPhase: number } {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    const wl = Math.max(1e-3, wavelength);
    const k = (2 * Math.PI) / wl;
    if (len === 0) return { points: [a, b], endPhase: phase0 };
    // Unit chord direction and its left normal (perpendicular).
    const tx = dx / len;
    const ty = dy / len;
    const nx = -ty;
    const ny = tx;
    // ~16 samples per wave cycle, with a sane floor so short chords still curve.
    const samplesPerWave = 16;
    const steps = Math.max(8, Math.ceil((len / wl) * samplesPerWave));
    const points: Vec2[] = [];
    for (let i = 0; i <= steps; i += 1) {
        const d = (len * i) / steps; // distance along the chord
        const off = amplitude * Math.sin(phase0 + k * d);
        points.push({
            x: a.x + tx * d + nx * off,
            y: a.y + ty * d + ny * off,
        });
    }
    return { points, endPhase: phase0 + k * len };
}

/**
 * Standalone (two-node) sine centerline. Phase starts and ends at zero so a
 * lone connection sits exactly on both nodes. Chains use `sineChainCenterline`
 * to carry phase through shared nodes instead.
 */
function sineCenterline(
    a: Vec2,
    b: Vec2,
    amplitude: number,
    wavelength: number,
): Vec2[] {
    const { points } = sineSegment(a, b, amplitude, wavelength, 0);
    if (points.length < 2) return [a, b];
    // Pin the exact endpoints to remove any float drift from sampling.
    points[0] = { ...a };
    points[points.length - 1] = { ...b };
    return points;
}

/**
 * Sine centerline spanning a whole chain of nodes, with the wave phase carried
 * continuously across each shared node so the wave flows through it instead of
 * snapping back to zero on both sides (which produced a sharp V at the node and,
 * once offset for parallel lines, the runaway-amplitude spikes). Each segment
 * still undulates perpendicular to its own chord, so the wave tracks direction
 * changes at nodes; only the phase is shared. Returns one concatenated polyline
 * (the duplicated shared node dropped between segments).
 *
 * For closed chains the seam is treated like any other node (no attempt to align
 * the wave's start and end phase) and softened by the corner radius, matching
 * the polyline/arc chain behavior.
 */
function sineChainCenterline(
    positions: Vec2[],
    amplitude: number,
    wavelength: number,
): Vec2[] {
    const centerline: Vec2[] = [];
    let phase = 0;
    for (let i = 0; i < positions.length - 1; i += 1) {
        const { points, endPhase } = sineSegment(
            positions[i]!,
            positions[i + 1]!,
            amplitude,
            wavelength,
            phase,
        );
        phase = endPhase;
        // Drop the first point of every segment after the first; it equals the
        // previous segment's last point (the shared node).
        centerline.push(...(i === 0 ? points : points.slice(1)));
    }
    if (centerline.length > 0) centerline[0] = { ...positions[0]! };
    if (centerline.length > 1) {
        centerline[centerline.length - 1] = {
            ...positions[positions.length - 1]!,
        };
    }
    return centerline;
}

/**
 * Ordered centerline points for a connection between two node centers.
 * `straight` returns the two endpoints; `orthogonal`/`diagonal` return a
 * staircase; `sine` returns a dense undulating polyline. Parallel offsetting and
 * rendering compose on top of whatever this returns, so adding a routing only
 * touches this function.
 */
export function connectionCenterline(
    a: Vec2,
    b: Vec2,
    routing: ConnectionRouting = "straight",
    bendCount = 1,
    sineAmplitude = 16,
    sineWavelength = 48,
): Vec2[] {
    switch (routing) {
        case "orthogonal":
            return orthogonalCenterline(a, b, bendCount);
        case "diagonal":
            return diagonalCenterline(a, b, bendCount);
        case "sine":
            return sineCenterline(a, b, sineAmplitude, sineWavelength);
        case "straight":
        default:
            return [a, b];
    }
}

/**
 * Cap on the miter scale factor `1/cos(theta/2)`. At a sharp turn that factor
 * grows without bound (→ ∞ as the turn approaches 180°), which would fling the
 * offset vertex far from the path — the spike seen where two independently
 * phased sine segments meet at a shared node. Past this limit we clamp the
 * miter length (matching how SVG's `stroke-miterlimit` falls back), keeping the
 * offset bounded at the cost of a slightly rounded-in corner there.
 */
const MITER_SCALE_LIMIT = 4;

/**
 * Per-vertex miter vectors for a polyline. Multiplying a vertex by `offset`
 * along its vector places it at the intersection of the two offset segment
 * lines (a miter join), so straight runs stay exactly parallel right up to a
 * corner instead of converging toward it.
 */
function polylineMiters(points: Vec2[], closed = false): Vec2[] {
    const n = points.length;
    if (n < 2) return points.map(() => ({ x: 0, y: 0 }));
    // Left normal of each segment (rotate the unit tangent by +90 degrees). For
    // a closed ring (unique vertices, no repeated seam) also add the wrap
    // segment last→first so every vertex — the seam included — has two segments.
    const segNormals: Vec2[] = [];
    const segCount = closed ? n : n - 1;
    for (let i = 0; i < segCount; i += 1) {
        const a = points[i]!;
        const b = points[(i + 1) % n]!;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        segNormals.push({ x: -dy / len, y: dx / len });
    }
    const out: Vec2[] = [];
    for (let i = 0; i < n; i += 1) {
        // Incoming segment ends at i, outgoing starts at i. On a ring the seam
        // vertex (i = 0) takes the wrap segment as its incoming one.
        const prev = segNormals[i - 1] ?? (closed ? segNormals[n - 1] : undefined);
        const next = segNormals[i];
        if (prev && next) {
            // Bisector direction (sum of unit normals), then scale by the miter
            // factor 1/cos(theta/2) so the offset distance stays constant on
            // both adjacent segments. cos(theta/2) is the dot of the unit
            // bisector with either segment normal.
            const bx = prev.x + next.x;
            const by = prev.y + next.y;
            const blen = Math.hypot(bx, by);
            if (blen < 1e-6) {
                // 180-degree reversal: fall back to one segment's normal.
                out.push({ ...next });
                continue;
            }
            const ux = bx / blen;
            const uy = by / blen;
            const cos = ux * next.x + uy * next.y; // = cos(theta/2)
            // Clamp the miter factor so a sharp turn can't fling the offset
            // vertex arbitrarily far (see MITER_SCALE_LIMIT).
            const scale =
                cos > 1e-6 ? Math.min(1 / cos, MITER_SCALE_LIMIT) : 1;
            out.push({ x: ux * scale, y: uy * scale });
        } else {
            out.push(prev ?? next ?? { x: 0, y: 0 });
        }
    }
    return out;
}

/**
 * Shift a polyline by `offset` units perpendicular to its direction, using
 * miter joins at corners so parallel offsets keep a constant distance.
 */
export function offsetPolyline(
    points: Vec2[],
    offset: number,
    closed = false,
): Vec2[] {
    if (offset === 0) return points;
    const miters = polylineMiters(points, closed);
    return points.map((p, i) => ({
        x: p.x + miters[i]!.x * offset,
        y: p.y + miters[i]!.y * offset,
    }));
}

/**
 * Compute a tangent fillet for the corner at `corner` between neighbors `prev`
 * and `next`, for a fillet of radius `radius`.
 *
 * The two arc endpoints `p1`/`p2` sit on the legs at a *trim* distance from the
 * corner. For the rounded part to be tangent to both edges, trim and the arc
 * radius are related by `trim = R / tan(theta/2)`, where theta is the interior
 * angle — using the radius as the trim (as a naive implementation does) is only
 * correct at 90 degrees, which is why other angles look off. The trim is
 * clamped to half the shorter leg; when clamped, the effective radius `r`
 * shrinks to stay tangent.
 */
function filletCorner(
    prev: Vec2,
    corner: Vec2,
    next: Vec2,
    radius: number,
): { p1: Vec2; p2: Vec2; r: number; sweep: number } {
    const v1x = prev.x - corner.x;
    const v1y = prev.y - corner.y;
    const v2x = next.x - corner.x;
    const v2y = next.y - corner.y;
    const len1 = Math.hypot(v1x, v1y) || 1;
    const len2 = Math.hypot(v2x, v2y) || 1;
    const u1x = v1x / len1;
    const u1y = v1y / len1;
    const u2x = v2x / len2;
    const u2y = v2y / len2;
    // Interior half-angle between the two legs.
    const cosTheta = Math.max(-1, Math.min(1, u1x * u2x + u1y * u2y));
    const half = Math.acos(cosTheta) / 2;
    const tanHalf = Math.tan(half);
    // Trim needed on each leg for a tangent fillet of `radius`, clamped so the
    // fillet fits both legs. Recover the effective radius after clamping.
    const wantTrim = tanHalf > 1e-6 ? radius / tanHalf : radius;
    const trim = Math.min(wantTrim, len1 / 2, len2 / 2);
    const r = trim * tanHalf;
    const p1 = { x: corner.x + u1x * trim, y: corner.y + u1y * trim };
    const p2 = { x: corner.x + u2x * trim, y: corner.y + u2y * trim };
    // Sweep direction: sign of the 2D cross product of the two legs.
    const cross = v1x * v2y - v1y * v2x;
    return { p1, p2, r, sweep: cross > 0 ? 0 : 1 };
}

/**
 * The SVG path command(s) that draw the softened part of a corner from its
 * incoming endpoint `p1` to its outgoing endpoint `p2`: an arc when rounding,
 * a straight line (chamfer) when beveling.
 */
function cornerCommand(
    p1: Vec2,
    p2: Vec2,
    r: number,
    sweep: number,
    style: CornerStyle,
): string {
    if (style === "bevel") {
        return `L${round2(p2.x)} ${round2(p2.y)}`;
    }
    return `A${round2(r)} ${round2(r)} 0 0 ${sweep} ${round2(p2.x)} ${round2(p2.y)}`;
}

/**
 * Serialize an open polyline to an SVG path `d` attribute. When `radius > 0`,
 * each interior corner is softened — rounded (arc) or beveled (chamfer) per
 * `style`. For a closed ring use `closedPolygonToPath`, which also softens the
 * seam corner.
 */
export function polylineToPath(
    points: Vec2[],
    radius = 0,
    style: CornerStyle = "round",
): string {
    if (points.length === 0) return "";
    if (points.length < 3 || radius <= 0) {
        return points
            .map(
                (p, i) =>
                    `${i === 0 ? "M" : "L"}${round2(p.x)} ${round2(p.y)}`,
            )
            .join(" ");
    }
    const cmds: string[] = [`M${round2(points[0]!.x)} ${round2(points[0]!.y)}`];
    for (let i = 1; i < points.length - 1; i += 1) {
        const { p1, p2, r, sweep } = filletCorner(
            points[i - 1]!,
            points[i]!,
            points[i + 1]!,
            radius,
        );
        cmds.push(`L${round2(p1.x)} ${round2(p1.y)}`);
        cmds.push(cornerCommand(p1, p2, r, sweep, style));
    }
    const last = points[points.length - 1]!;
    cmds.push(`L${round2(last.x)} ${round2(last.y)}`);
    return cmds.join(" ");
}

/**
 * Points along a fillet arc from `p1` to `p2` (radius `r`), excluding `p1`,
 * ending exactly on `p2`. `corner` is the sharp vertex the fillet rounds: the
 * arc always bulges *away* from it (it curves toward the inside of the bend),
 * which uniquely picks the circle center — no reliance on the SVG sweep flag,
 * whose two candidate centers gave the same endpoints but opposite bulges (one
 * cutting *inside* the corner — the chord-across bug).
 */
function filletArcPoints(
    p1: Vec2,
    p2: Vec2,
    r: number,
    corner: Vec2,
    steps: number,
): Vec2[] {
    if (r <= 0) return [p2];
    const mx = (p1.x + p2.x) / 2;
    const my = (p1.y + p2.y) / 2;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const chord = Math.hypot(dx, dy);
    if (chord < 1e-9) return [p2];
    const half = chord / 2;
    const hSq = r * r - half * half;
    const h = hSq > 0 ? Math.sqrt(hSq) : 0;
    // The two candidate centers sit at mid ± n·h along the chord normal. The
    // fillet center is the one on the OPPOSITE side of the chord from the sharp
    // corner (the arc curves away from the corner), so pick the center farther
    // from `corner`.
    const nx = -dy / chord;
    const ny = dx / chord;
    const cA = { x: mx + nx * h, y: my + ny * h };
    const cB = { x: mx - nx * h, y: my - ny * h };
    const dA = (cA.x - corner.x) ** 2 + (cA.y - corner.y) ** 2;
    const dB = (cB.x - corner.x) ** 2 + (cB.y - corner.y) ** 2;
    const c = dA >= dB ? cA : cB;
    const a1 = Math.atan2(p1.y - c.y, p1.x - c.x);
    const a2 = Math.atan2(p2.y - c.y, p2.x - c.x);
    // Walk the minor arc (|delta| ≤ π) on this circle — can't loop or chord.
    let delta = a2 - a1;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    const out: Vec2[] = [];
    for (let i = 1; i <= steps; i += 1) {
        const ang = a1 + delta * (i / steps);
        out.push({ x: c.x + r * Math.cos(ang), y: c.y + r * Math.sin(ang) });
    }
    return out;
}

/** Steps used to flatten one softened corner's fillet arc into points. */
const FILLET_FLATTEN_STEPS = 10;

/**
 * Flatten an open polyline into points, softening each interior corner exactly
 * as `polylineToPath` does (rounded arc or beveled chamfer per `style`,
 * radius `radius`). This is the point-list twin of `polylineToPath`, used so
 * objects populated along a connection ride the same softened corners the
 * stroke draws. With `radius <= 0` or fewer than 3 points it returns the input
 * unchanged.
 */
export function polylineToPoints(
    points: Vec2[],
    radius = 0,
    style: CornerStyle = "round",
): Vec2[] {
    if (points.length < 3 || radius <= 0) return points;
    const out: Vec2[] = [points[0]!];
    for (let i = 1; i < points.length - 1; i += 1) {
        const corner = points[i]!;
        const { p1, p2, r } = filletCorner(
            points[i - 1]!,
            corner,
            points[i + 1]!,
            radius,
        );
        out.push(p1);
        if (style === "bevel") {
            out.push(p2);
        } else {
            out.push(
                ...filletArcPoints(p1, p2, r, corner, FILLET_FLATTEN_STEPS),
            );
        }
    }
    out.push(points[points.length - 1]!);
    return out;
}

/**
 * Flatten a closed ring (unique vertices, no repeated seam) into points,
 * softening every corner — the seam included — exactly as `closedPolygonToPath`
 * draws it. The point-list twin of `closedPolygonToPath`, used so objects on a
 * closed bridged chain ride the same softened loop the stroke draws. The result
 * repeats its first point at the end so callers can treat it as a closed run.
 */
export function closedPolygonToPoints(
    points: Vec2[],
    radius = 0,
    style: CornerStyle = "round",
): Vec2[] {
    const n = points.length;
    if (n < 3 || radius <= 0) return n > 0 ? [...points, points[0]!] : points;
    const out: Vec2[] = [];
    for (let i = 0; i < n; i += 1) {
        const corner = points[i]!;
        const { p1, p2, r } = filletCorner(
            points[(i - 1 + n) % n]!,
            corner,
            points[(i + 1) % n]!,
            radius,
        );
        out.push(p1);
        if (style === "bevel") {
            out.push(p2);
        } else {
            out.push(
                ...filletArcPoints(p1, p2, r, corner, FILLET_FLATTEN_STEPS),
            );
        }
    }
    // Close the loop back onto the first corner's incoming endpoint.
    out.push(out[0]!);
    return out;
}

/**
 * Serialize a closed polygon to an SVG path `d` attribute (with trailing `Z`).
 * When `radius > 0`, every corner — including the one at the start/end seam —
 * is softened (rounded arc or beveled chamfer per `style`).
 */
export function closedPolygonToPath(
    points: Vec2[],
    radius = 0,
    style: CornerStyle = "round",
): string {
    const n = points.length;
    if (n === 0) return "";
    if (n < 3 || radius <= 0) {
        return (
            points
                .map(
                    (p, i) =>
                        `${i === 0 ? "M" : "L"}${round2(p.x)} ${round2(p.y)}`,
                )
                .join(" ") + " Z"
        );
    }
    const cmds: string[] = [];
    for (let i = 0; i < n; i += 1) {
        const prev = points[(i - 1 + n) % n]!;
        const corner = points[i]!;
        const next = points[(i + 1) % n]!;
        const { p1, p2, r, sweep } = filletCorner(prev, corner, next, radius);
        // First corner opens the path at its incoming endpoint.
        cmds.push(
            `${i === 0 ? "M" : "L"}${round2(p1.x)} ${round2(p1.y)}`,
        );
        cmds.push(cornerCommand(p1, p2, r, sweep, style));
    }
    cmds.push("Z");
    return cmds.join(" ");
}

/**
 * SVG `d` for a single circular arc from `a` to `b`, bulging to one side
 * (`flip` chooses which). The endpoints stay pinned regardless of `sweep`.
 *
 * `sweepFraction` (0..1, default 0.5) controls how deep the bulge is by sizing
 * the imaginary circle the arc rides on:
 * - 0.5 is a semicircle — the chord equals the circle's diameter (the nodes sit
 *   at opposite ends, the deepest a minor arc can be).
 * - Below 0.5 the chord is a smaller fraction of the diameter (chord/diameter =
 *   2·sweep), so the circle grows and the arc flattens toward a straight line.
 *   At 0.25 the chord is half the diameter (radius = the chord length).
 * - Above 0.5 the arc passes the diameter and takes the *major* arc (>180°),
 *   bulging into a progressively deeper loop.
 *
 * `radiusDelta` grows/shrinks the radius for concentric parallel copies, which
 * still share the chord endpoints but bulge farther out / flatter.
 */
function arcPath(
    a: Vec2,
    b: Vec2,
    flip: boolean,
    sweepFraction = 0.5,
    radiusDelta = 0,
): string {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const chord = Math.hypot(dx, dy);
    if (chord < 1e-6) {
        return `M${round2(a.x)} ${round2(a.y)}`;
    }
    const s = Math.min(Math.max(sweepFraction, 0.01), 0.99);
    // Base radius from chord = 2r·sin(θ/2). Below the semicircle we size the
    // circle by the chord/diameter ratio (= 2·s), matching "the chord is this
    // fraction of the diameter"; at s=0.5 that's a full diameter → r = chord/2.
    // Above the semicircle we instead sweep the central angle past 180° and take
    // the major arc, so the bulge keeps deepening.
    let r: number;
    let largeArc: 0 | 1;
    if (s <= 0.5) {
        const ratio = 2 * s; // chord / diameter, 0..1
        r = chord / (2 * ratio);
        largeArc = 0;
    } else {
        const theta = s * 2 * Math.PI; // central angle, 180°..360°
        r = chord / (2 * Math.sin(theta / 2));
        largeArc = 1;
    }
    r = Math.max(0.01, r + radiusDelta);
    // The sweep flag picks the side; flipping it mirrors the arc to the other.
    const sweep = flip ? 1 : 0;
    return (
        `M${round2(a.x)} ${round2(a.y)} ` +
        `A${round2(r)} ${round2(r)} 0 ${largeArc} ${sweep} ` +
        `${round2(b.x)} ${round2(b.y)}`
    );
}

/**
 * One `d` string per parallel line for an `arc` routing. Three layouts:
 * - concentric (`spacedEnds` false): every arc shares the same two endpoints
 *   and fans out via differing radii — they touch at the ends, gap in the
 *   middle.
 * - spaced ends, perpendicular (`spacedEnds` true, `endsAlongChord` false):
 *   each arc's endpoints are shifted perpendicular to the chord by its offset
 *   and the radius is held constant, so the arcs stay `spacing` apart all the
 *   way to the endpoints. For a horizontal pair the endpoints stack vertically.
 * - spaced ends, along chord (`spacedEnds` true, `endsAlongChord` true): each
 *   arc's endpoints are shifted *along* the chord, in opposite directions on
 *   the two nodes, so the chords nest concentrically and the endpoints stack
 *   along the connection line (horizontally for a horizontal pair).
 */
function arcPaths(
    a: Vec2,
    b: Vec2,
    count: number,
    spacing: number,
    flip: boolean,
    spacedEnds: boolean,
    endsAlongChord: boolean,
    sweepFraction: number,
): string[] {
    if (count <= 1 || spacing === 0) {
        return [arcPath(a, b, flip, sweepFraction)];
    }

    if (!spacedEnds) {
        // Concentric: vary the radius, keep the endpoints fixed.
        const paths: string[] = [];
        for (let i = 0; i < count; i += 1) {
            const delta = (i - (count - 1) / 2) * spacing;
            paths.push(arcPath(a, b, flip, sweepFraction, delta));
        }
        return paths;
    }

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;

    if (endsAlongChord) {
        // Spaced ends along the chord: push each endpoint outward along the a→b
        // direction in opposite senses (a backward, b forward), so outer copies
        // span a longer chord and the arcs nest concentrically. Endpoints stack
        // along the connection line rather than perpendicular to it.
        const ux = dx / len;
        const uy = dy / len;
        const paths: string[] = [];
        for (let i = 0; i < count; i += 1) {
            const offset = (i - (count - 1) / 2) * spacing;
            const a2 = { x: a.x - ux * offset, y: a.y - uy * offset };
            const b2 = { x: b.x + ux * offset, y: b.y + uy * offset };
            paths.push(arcPath(a2, b2, flip, sweepFraction));
        }
        return paths;
    }

    // Spaced ends, perpendicular: shift each copy's endpoints perpendicular to
    // the chord, so the whole arc (endpoints included) stays a constant
    // `spacing` apart.
    // Left normal of the a→b direction (matches offsetPolyline's convention).
    const nx = -dy / len;
    const ny = dx / len;
    const paths: string[] = [];
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        const a2 = { x: a.x + nx * offset, y: a.y + ny * offset };
        const b2 = { x: b.x + nx * offset, y: b.y + ny * offset };
        paths.push(arcPath(a2, b2, flip, sweepFraction));
    }
    return paths;
}

/**
 * One `d` string per parallel line for a connection. The centerline is routed
 * per `routing`/`bendCount`, then `lineCount` copies are offset `lineSpacing`
 * apart (straddling the centerline) and corners filleted by `cornerRadius`.
 * `arc` is special-cased: it draws true (concentric) semicircles rather than
 * offsetting a polyline.
 */
export function connectionPaths(
    a: Vec2,
    b: Vec2,
    style?: Pick<
        ResolvedConnectionStyle,
        | "lineCount"
        | "lineSpacing"
        | "routing"
        | "bendCount"
        | "cornerRadius"
        | "cornerStyle"
        | "arcFlip"
        | "arcSpacedEnds"
        | "arcEndsAlongChord"
        | "arcSweep"
        | "sineAmplitude"
        | "sineWavelength"
    >,
): string[] {
    const count = Math.max(1, Math.floor(style?.lineCount ?? 1));
    const spacing = style?.lineSpacing ?? 0;
    if (style?.routing === "arc") {
        return arcPaths(
            a,
            b,
            count,
            spacing,
            style?.arcFlip ?? false,
            style?.arcSpacedEnds ?? false,
            style?.arcEndsAlongChord ?? false,
            style?.arcSweep ?? 0.5,
        );
    }
    const centerline = connectionCenterline(
        a,
        b,
        style?.routing ?? "straight",
        style?.bendCount ?? 1,
        style?.sineAmplitude ?? 16,
        style?.sineWavelength ?? 48,
    );
    // Fillets apply to bent routings only; `sine` is already smooth and its
    // dense samples would just be no-op-clamped, so skip it too.
    const radius =
        style?.routing &&
        style.routing !== "straight" &&
        style.routing !== "sine"
            ? style.cornerRadius ?? 0
            : 0;
    const corner = style?.cornerStyle ?? "round";
    if (count === 1 || spacing === 0) {
        return [polylineToPath(centerline, radius, corner)];
    }
    const paths: string[] = [];
    // Center the bundle: offsets are symmetric about 0.
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        paths.push(
            polylineToPath(offsetPolyline(centerline, offset), radius, corner),
        );
    }
    return paths;
}

// --- bridged connection chains ----------------------------------------------
//
// When a preset opts into `bridge`, connections using it are stitched into
// continuous chains: a node where exactly two bridged connections meet (and
// they agree on `lineCount` and `routing`) becomes an interior bend the line
// flows through, instead of two segments dead-ending at its center. Detection
// (`bridgedConnectionChains`) and geometry (`chainConnectionPaths`) are the two
// halves; both render sites (viewer + exporter) consume them so the editor and
// the exported SVG stay identical.

/** An ordered run of connections joined through shared nodes. */
export interface ConnectionChain {
    /** Connection ids spanning the chain, in walk order. */
    connectionIds: string[];
    /** Node ids visited, in order (length = connectionIds.length + 1). */
    nodeIds: string[];
    /** Node center positions, in order (parallel to `nodeIds`). */
    positions: Vec2[];
    /**
     * True when the chain is a closed loop — its first and last node are the
     * same (e.g. A→B→C→A). The shared node is then an interior bridge junction
     * like any other, so the closing segment (last→first) is corner/tangent
     * matched too instead of dead-ending. `nodeIds`/`positions` keep the
     * duplicated wrap node at both ends so segment indexing stays uniform.
     */
    closed?: boolean;
    /** The resolved style (routing + stroke) shared by every connection. */
    preset: ResolvedConnectionStyle;
    /**
     * Per-connection arc overrides keyed by the connection's index in the
     * chain. Only meaningful for a length-1 (unbridged) chain — inside a
     * bridged arc chain the sweep is solved for tangent continuity, so
     * per-connection sweep is ignored (flip still picks the common bulge side).
     */
    arcFlip?: boolean;
    arcSweep?: number;
}

/** A connection paired with its resolved (routing + stroke) style. */
interface ResolvedConnection {
    conn: Connection;
    preset: ResolvedConnectionStyle;
}

/**
 * Group a document's connections into chains for rendering.
 *
 * Connections whose preset has `bridge: false` (or whose preset/endpoints are
 * missing, or whose endpoints are hidden) each become a standalone length-1
 * chain — identical to the old per-connection rendering. Connections whose
 * preset opts into bridging are walked into longer chains: two bridged
 * connections are joined at a node only when that node has exactly two bridged
 * connections incident to it (degree 2) and they agree on `lineCount` and
 * `routing`. A node with three-plus bridged connections, or a routing/count
 * mismatch, breaks the chain there (both sides still render, just not joined).
 *
 * `nodeById`/`presetById` are passed in so callers reuse the maps they already
 * build; `strokeStyles` is used to resolve each preset's referenced stroke style
 * into the flat style the chain carries. `nodeVisible` lets a caller drop
 * connections touching hidden nodes (the exporter does; the live viewer keeps
 * them visible-but-faint upstream).
 */
export function bridgedConnectionChains(
    doc: DiagramDocument,
    nodeById: Map<string, DiagramNode>,
    presetById: Map<string, ConnectionPreset>,
    nodeVisible?: (node: DiagramNode) => boolean,
): ConnectionChain[] {
    const visible = nodeVisible ?? (() => true);
    const standalone: ConnectionChain[] = [];
    const bridgeable: ResolvedConnection[] = [];

    // First pass: split connections into "renders standalone" vs "can bridge".
    for (const conn of doc.connections) {
        const a = nodeById.get(conn.sourceId);
        const b = nodeById.get(conn.targetId);
        const rawPreset = presetById.get(conn.presetId);
        if (!a || !b || !rawPreset || !visible(a) || !visible(b)) continue;
        const preset = resolveConnectionStyle(rawPreset, doc.strokeStyles);
        if (!preset.bridge || a.id === b.id) {
            standalone.push({
                connectionIds: [conn.id],
                nodeIds: [a.id, b.id],
                positions: [a.position, b.position],
                preset,
                arcFlip: conn.arcFlip,
                arcSweep: conn.arcSweep,
            });
            continue;
        }
        bridgeable.push({ conn, preset });
    }

    // Build incidence: for each node, the bridgeable connections touching it.
    const incidence = new Map<string, ResolvedConnection[]>();
    for (const rc of bridgeable) {
        for (const nodeId of [rc.conn.sourceId, rc.conn.targetId]) {
            const list = incidence.get(nodeId);
            if (list) list.push(rc);
            else incidence.set(nodeId, [rc]);
        }
    }

    /**
     * True when a node may bridge two connections: exactly two bridgeable
     * connections meet there and they agree on the bridge-relevant style
     * (parallel-line count and routing). Otherwise the node is a chain end.
     */
    const isBridgeJunction = (nodeId: string): boolean => {
        const list = incidence.get(nodeId);
        if (!list || list.length !== 2) return false;
        const [x, y] = list as [ResolvedConnection, ResolvedConnection];
        return (
            x.preset.lineCount === y.preset.lineCount &&
            x.preset.routing === y.preset.routing
        );
    };

    /** The other endpoint of a connection from `nodeId`. */
    const otherEnd = (rc: ResolvedConnection, nodeId: string): string =>
        rc.conn.sourceId === nodeId ? rc.conn.targetId : rc.conn.sourceId;

    const chains: ConnectionChain[] = [...standalone];
    const usedConns = new Set<string>();

    /** Walk one direction from (startNode → via rc), extending `acc`. */
    const walk = (
        startNode: string,
        firstConn: ResolvedConnection,
        acc: { connIds: string[]; nodeIds: string[] },
        forward: boolean,
    ): void => {
        let node = startNode;
        let rc: ResolvedConnection | undefined = firstConn;
        while (rc && !usedConns.has(rc.conn.id)) {
            usedConns.add(rc.conn.id);
            const next = otherEnd(rc, node);
            if (forward) {
                acc.connIds.push(rc.conn.id);
                acc.nodeIds.push(next);
            } else {
                acc.connIds.unshift(rc.conn.id);
                acc.nodeIds.unshift(next);
            }
            node = next;
            // Continue only if `node` is a clean degree-2 junction; pick the
            // incident connection that isn't the one we just traversed.
            if (!isBridgeJunction(node)) break;
            const pair = incidence.get(node)!;
            rc = pair.find((c) => !usedConns.has(c.conn.id));
        }
    };

    // Seed chains from chain *ends* (non-junction nodes) so each chain is walked
    // start-to-finish once. A pure cycle (no ends) is handled by the leftover
    // sweep afterward.
    for (const rc of bridgeable) {
        if (usedConns.has(rc.conn.id)) continue;
        // Prefer to start where one side is a chain end.
        const sIsEnd = !isBridgeJunction(rc.conn.sourceId);
        const tIsEnd = !isBridgeJunction(rc.conn.targetId);
        if (!sIsEnd && !tIsEnd) continue; // interior of a chain/cycle; later
        const start = sIsEnd ? rc.conn.sourceId : rc.conn.targetId;
        const acc = { connIds: [], nodeIds: [start] } as {
            connIds: string[];
            nodeIds: string[];
        };
        walk(start, rc, acc, true);
        chains.push(buildChain(acc, nodeById, rc.preset));
    }

    // Any connections left belong to cycles (every node a junction). Break each
    // arbitrarily by starting at one endpoint and walking until we loop back.
    for (const rc of bridgeable) {
        if (usedConns.has(rc.conn.id)) continue;
        const start = rc.conn.sourceId;
        const acc = { connIds: [], nodeIds: [start] } as {
            connIds: string[];
            nodeIds: string[];
        };
        walk(start, rc, acc, true);
        chains.push(buildChain(acc, nodeById, rc.preset));
    }

    return chains;
}

/** Assemble a ConnectionChain from accumulated ids + the shared preset. */
function buildChain(
    acc: { connIds: string[]; nodeIds: string[] },
    nodeById: Map<string, DiagramNode>,
    preset: ResolvedConnectionStyle,
): ConnectionChain {
    // A chain whose first and last node coincide (and that spans more than one
    // node) is a closed loop — the cycle sweep walks back to its start.
    const closed =
        acc.nodeIds.length > 2 &&
        acc.nodeIds[0] === acc.nodeIds[acc.nodeIds.length - 1];
    return {
        connectionIds: acc.connIds,
        nodeIds: acc.nodeIds,
        positions: acc.nodeIds.map((id) => nodeById.get(id)!.position),
        closed,
        preset,
    };
}

/** Normalize an angle to (-π, π]. */
function normalizeAngle(a: number): number {
    let x = a;
    while (x <= -Math.PI) x += 2 * Math.PI;
    while (x > Math.PI) x -= 2 * Math.PI;
    return x;
}

/**
 * `d` strings for a whole connection chain, one per parallel line.
 *
 * Length-1 chains delegate straight to `connectionPaths` (with any
 * per-connection arc overrides applied), so unbridged connections render
 * exactly as before. Multi-segment chains build continuous geometry:
 * - polyline routings (straight/orthogonal/diagonal) concatenate every
 *   segment's centerline into one polyline, then offset + fillet it as a unit,
 *   so each parallel line is a single continuous bent path through every node.
 * - `arc` routing computes a tangent-matched sweep per segment (the interior
 *   bend at each junction is split evenly between its two arcs) so consecutive
 *   arcs meet without a kink, then emits true SVG arc commands per segment.
 */
export function chainConnectionPaths(chain: ConnectionChain): string[] {
    const { positions, preset } = chain;
    if (positions.length < 2) return [];
    if (positions.length === 2) {
        // Standalone connection: honor per-connection arc overrides.
        const style =
            chain.arcFlip !== undefined || chain.arcSweep !== undefined
                ? {
                      ...preset,
                      ...(chain.arcFlip !== undefined
                          ? { arcFlip: chain.arcFlip }
                          : {}),
                      ...(chain.arcSweep !== undefined
                          ? { arcSweep: chain.arcSweep }
                          : {}),
                  }
                : preset;
        return connectionPaths(positions[0]!, positions[1]!, style);
    }

    if (preset.routing === "arc") {
        return arcChainPaths(positions, preset, chain.closed ?? false);
    }
    return polylineChainPaths(positions, preset, chain.closed ?? false);
}

/**
 * Continuous parallel paths for a multi-node chain of a polyline routing.
 * Each segment is routed independently into its centerline points, then the
 * per-segment polylines are concatenated (dropping the duplicated shared node)
 * into one polyline spanning the whole chain. That single polyline is offset
 * per parallel line and filleted, so corners at the shared nodes are softened
 * by the same `cornerRadius` as routing bends.
 */
function polylineChainPaths(
    positions: Vec2[],
    preset: ResolvedConnectionStyle,
    closed = false,
): string[] {
    // Sine routing carries its wave phase continuously through shared nodes, so
    // it builds the whole chain at once rather than per-segment (which would
    // reset the wave to zero at every node and kink there).
    const centerline: Vec2[] =
        preset.routing === "sine"
            ? sineChainCenterline(
                  positions,
                  preset.sineAmplitude,
                  preset.sineWavelength,
              )
            : [];
    if (preset.routing !== "sine") {
        for (let i = 0; i < positions.length - 1; i += 1) {
            const seg = connectionCenterline(
                positions[i]!,
                positions[i + 1]!,
                preset.routing,
                preset.bendCount,
                preset.sineAmplitude,
                preset.sineWavelength,
            );
            // Drop the first point of every segment after the first; it equals
            // the previous segment's last point (the shared node).
            centerline.push(...(i === 0 ? seg : seg.slice(1)));
        }
    }
    // A closed chain's centerline repeats the seam node at both ends; drop the
    // duplicate so `closedPolygonToPath`/`offsetPolyline` see unique ring
    // vertices (the seam corner is then softened like any other).
    if (closed && centerline.length > 1) centerline.pop();
    // Straight chains have interior corners only at the shared nodes; soften
    // those with the preset's corner radius too (not just bent routings).
    const radius = preset.cornerRadius ?? 0;
    const corner = preset.cornerStyle ?? "round";
    const count = Math.max(1, Math.floor(preset.lineCount ?? 1));
    const spacing = preset.lineSpacing ?? 0;
    const toPath = (pts: Vec2[]): string =>
        closed
            ? closedPolygonToPath(pts, radius, corner)
            : polylineToPath(pts, radius, corner);
    if (count === 1 || spacing === 0) {
        return [toPath(centerline)];
    }
    const paths: string[] = [];
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        paths.push(toPath(offsetPolyline(centerline, offset, closed)));
    }
    return paths;
}

/**
 * Continuous parallel paths for a multi-node chain of `arc` routing, with
 * tangents matched at each interior node so consecutive arcs meet smoothly.
 *
 * For each interior node the turn between its incoming and outgoing chords is
 * split evenly between the two arcs (`ψ = ½·turn`), giving each arc a
 * tangent-chord angle at that end. A single circular arc is symmetric (same
 * angle at both ends), so for a segment touched by two junctions we average the
 * two demanded angles — exact for a single junction (3-node chain), an
 * approximation for longer chains (a biarc split would make it exact; noted as
 * a follow-up). Each segment is then drawn with the sweep that yields its
 * solved angle, reusing the existing per-segment parallel-line layouts.
 */
function arcChainPaths(
    positions: Vec2[],
    preset: ResolvedConnectionStyle,
    closed = false,
): string[] {
    const segCount = positions.length - 1;
    // Chord direction of each segment.
    const chordDir: number[] = [];
    for (let i = 0; i < segCount; i += 1) {
        const a = positions[i]!;
        const b = positions[i + 1]!;
        chordDir.push(Math.atan2(b.y - a.y, b.x - a.x));
    }
    // Demanded tangent-chord angle (ψ) at each end of each segment, signed so a
    // positive ψ bulges to the `flip=false` side. Interior junctions split the
    // turn evenly; the two outer chain ends keep the preset's own sweep so the
    // chain doesn't flatten at its tips.
    const presetPsi = (preset.arcSweep ?? 0.5) * Math.PI;
    // psiStart[i] / psiEnd[i] are ψ at the start / end of segment i.
    const psiStart: number[] = new Array(segCount).fill(presetPsi);
    const psiEnd: number[] = new Array(segCount).fill(presetPsi);
    for (let j = 1; j < positions.length - 1; j += 1) {
        // Interior node j joins segment (j-1) [ending here] and segment j
        // [starting here]. Even split of the turn between the two chords.
        const turn = normalizeAngle(chordDir[j]! - chordDir[j - 1]!);
        const half = turn / 2;
        // ψ at the END of the incoming segment and the START of the outgoing
        // one are set so the two arcs share a tangent at node j.
        psiEnd[j - 1] = half;
        psiStart[j] = half;
    }
    if (closed && segCount > 1) {
        // The seam node closes the loop: the last segment ends there and the
        // first segment starts there. Split that turn evenly too so the loop
        // is tangent-matched all the way around instead of flattening at its
        // seam (the bug a 3+-node cycle hit).
        const turn = normalizeAngle(chordDir[0]! - chordDir[segCount - 1]!);
        const half = turn / 2;
        psiEnd[segCount - 1] = half;
        psiStart[0] = half;
    }
    // Each circular arc needs one ψ; average its two ends' demands. flip is
    // applied via the sweep flag in arcPaths, so fold the sign of ψ into flip.
    const count = Math.max(1, Math.floor(preset.lineCount ?? 1));
    const spacing = preset.lineSpacing ?? 0;
    // Collect per-line arrays so a chain emits `count` continuous-looking
    // bundles (one path string per line, segments concatenated).
    const perLine: string[][] = Array.from({ length: count }, () => []);
    for (let i = 0; i < segCount; i += 1) {
        const psi = (psiStart[i]! + psiEnd[i]!) / 2;
        // A negative ψ means the arc bulges to the opposite (flip) side; map the
        // magnitude to a sweep fraction and the sign to the flip flag.
        const flip = preset.arcFlip !== (psi < 0);
        const sweepFraction = Math.min(
            0.99,
            Math.max(0.01, Math.abs(psi) / Math.PI),
        );
        const segPaths = arcPaths(
            positions[i]!,
            positions[i + 1]!,
            count,
            spacing,
            flip,
            preset.arcSpacedEnds ?? false,
            preset.arcEndsAlongChord ?? false,
            sweepFraction,
        );
        segPaths.forEach((d, line) => perLine[line]!.push(d));
    }
    // Join each line's segments into one path string. Each segment starts with
    // its own M; SVG renders the concatenation as one visually continuous
    // (tangent-matched) stroke per line.
    return perLine.map((segs) => segs.join(" "));
}

// --- connection object population --------------------------------------------
//
// An "objects"/"both" connection clones SVG object(s) along the same geometry
// the stroke would follow. The line is first turned into one flat polyline per
// parallel line (`connectionLinePolylines`), reusing the exact routing/offset/
// arc math used for stroking, then each polyline is walked by arc-length
// (`samplePolylineByLength`) to drop instances at a fixed `objectSpacing`. This
// is fully analytic — no DOM mounting — so the viewer and the string exporter
// produce identical placements.

/** Number of straight segments used to flatten one arc into a polyline. */
const ARC_FLATTEN_STEPS = 48;

/**
 * Flatten a single circular arc (the same one `arcPath` would draw from `a` to
 * `b`) into a list of points. Mirrors `arcPath`'s radius/large-arc/side math so
 * objects ride exactly on the stroked curve.
 */
function arcToPoints(
    a: Vec2,
    b: Vec2,
    flip: boolean,
    sweepFraction: number,
    radiusDelta = 0,
): Vec2[] {
    const chord = Math.hypot(b.x - a.x, b.y - a.y);
    if (chord < 1e-6) return [a, b];
    const s = Math.min(Math.max(sweepFraction, 0.01), 0.99);
    let r: number;
    let largeArc: 0 | 1;
    if (s <= 0.5) {
        const ratio = 2 * s; // chord / diameter
        r = chord / (2 * ratio);
        largeArc = 0;
    } else {
        const theta = s * 2 * Math.PI;
        r = chord / (2 * Math.sin(theta / 2));
        largeArc = 1;
    }
    r = Math.max(0.01, r + radiusDelta);
    // Sweep flag picks the side (matches arcPath: flip → 1, else 0).
    const sweep: 0 | 1 = flip ? 1 : 0;
    // Reconstruct the same arc SVG's `A` command draws via the spec's
    // endpoint-to-center conversion (no x-rotation, rx=ry=r), then walk its
    // central angle. Reproducing SVG's exact (largeArc, sweep) center choice is
    // what keeps the flattened points on the visible stroke for every sweep
    // depth — a midpoint-perpendicular shortcut only matches at the semicircle.
    const x1p = (a.x - b.x) / 2;
    const y1p = (a.y - b.y) / 2;
    let rx = r;
    let ry = r;
    const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
    if (lambda > 1) {
        const sc = Math.sqrt(lambda);
        rx *= sc;
        ry *= sc;
    }
    const sign = largeArc !== sweep ? 1 : -1;
    const num =
        rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
    const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
    const co = sign * Math.sqrt(Math.max(0, num / den));
    const cxp = (co * (rx * y1p)) / ry;
    const cyp = (co * (-ry * x1p)) / rx;
    const cx = cxp + (a.x + b.x) / 2;
    const cy = cyp + (a.y + b.y) / 2;
    const startAng = Math.atan2((y1p - cyp) / ry, (x1p - cxp) / rx);
    let delta =
        Math.atan2((-y1p - cyp) / ry, (-x1p - cxp) / rx) - startAng;
    if (sweep === 0 && delta > 0) delta -= 2 * Math.PI;
    if (sweep === 1 && delta < 0) delta += 2 * Math.PI;
    const pts: Vec2[] = [];
    for (let i = 0; i <= ARC_FLATTEN_STEPS; i += 1) {
        const ang = startAng + delta * (i / ARC_FLATTEN_STEPS);
        pts.push({ x: cx + rx * Math.cos(ang), y: cy + ry * Math.sin(ang) });
    }
    return pts;
}

/**
 * One flat polyline per parallel line for a connection chain, in the same
 * geometry the stroke uses. Polyline routings concatenate their (offset)
 * centerlines; `arc` routing flattens each arc segment into points. Used to
 * populate objects along the path.
 */
export function connectionLinePolylines(chain: ConnectionChain): Vec2[][] {
    const { positions, preset } = chain;
    if (positions.length < 2) return [];
    const closed = chain.closed ?? false;
    const count = Math.max(1, Math.floor(preset.lineCount ?? 1));
    const spacing = preset.lineSpacing ?? 0;

    if (preset.routing === "arc") {
        // Resolve per-segment sweep/flip exactly as arcChainPaths does, but emit
        // points instead of `d`. For a standalone (2-node) chain honor the
        // per-connection arc overrides carried on the chain.
        const segCount = positions.length - 1;
        const chordDir: number[] = [];
        for (let i = 0; i < segCount; i += 1) {
            const a = positions[i]!;
            const b = positions[i + 1]!;
            chordDir.push(Math.atan2(b.y - a.y, b.x - a.x));
        }
        const baseFlip =
            positions.length === 2 && chain.arcFlip !== undefined
                ? chain.arcFlip
                : (preset.arcFlip ?? false);
        const baseSweep =
            positions.length === 2 && chain.arcSweep !== undefined
                ? chain.arcSweep
                : (preset.arcSweep ?? 0.5);
        const presetPsi = baseSweep * Math.PI;
        const psiStart: number[] = new Array(segCount).fill(presetPsi);
        const psiEnd: number[] = new Array(segCount).fill(presetPsi);
        for (let j = 1; j < positions.length - 1; j += 1) {
            const turn = normalizeAngle(chordDir[j]! - chordDir[j - 1]!);
            const half = turn / 2;
            psiEnd[j - 1] = half;
            psiStart[j] = half;
        }
        if (closed && segCount > 1) {
            // Match the seam junction too (mirrors arcChainPaths) so objects on
            // a closed chain ride the same tangent-matched loop the stroke does.
            const turn = normalizeAngle(chordDir[0]! - chordDir[segCount - 1]!);
            const half = turn / 2;
            psiEnd[segCount - 1] = half;
            psiStart[0] = half;
        }
        const lines: Vec2[][] = Array.from({ length: count }, () => []);
        for (let i = 0; i < segCount; i += 1) {
            const psi =
                segCount === 1
                    ? presetPsi
                    : (psiStart[i]! + psiEnd[i]!) / 2;
            const flip = baseFlip !== (psi < 0);
            const sweepFraction = Math.min(
                0.99,
                Math.max(0.01, Math.abs(psi) / Math.PI),
            );
            const a = positions[i]!;
            const b = positions[i + 1]!;
            for (let line = 0; line < count; line += 1) {
                const delta =
                    count > 1 && spacing !== 0
                        ? (line - (count - 1) / 2) * spacing
                        : 0;
                const segPts = arcToPoints(a, b, flip, sweepFraction, delta);
                // Drop the duplicated shared endpoint between segments.
                lines[line]!.push(...(i === 0 ? segPts : segPts.slice(1)));
            }
        }
        return lines;
    }

    // Polyline routings: build the centerline once, then offset per line and
    // soften corners to match the stroke. Corner radius applies to bent
    // routings (orthogonal/diagonal) for a 2-node connection, and — as the
    // stroke does — to the shared-node corners of a multi-node bridged chain
    // even when straight. Each offset line is filleted independently, exactly
    // like the stroke, so objects ride the same softened path.
    const routing = preset.routing ?? "straight";
    // Sine carries its wave phase continuously across shared nodes (see
    // sineChainCenterline); other routings concatenate per-segment centerlines.
    const centerline: Vec2[] =
        routing === "sine"
            ? sineChainCenterline(
                  positions,
                  preset.sineAmplitude,
                  preset.sineWavelength,
              )
            : [];
    if (routing !== "sine") {
        for (let i = 0; i < positions.length - 1; i += 1) {
            const seg = connectionCenterline(
                positions[i]!,
                positions[i + 1]!,
                routing,
                preset.bendCount ?? 1,
                preset.sineAmplitude,
                preset.sineWavelength,
            );
            centerline.push(...(i === 0 ? seg : seg.slice(1)));
        }
    }
    // A closed chain repeats its seam node; drop the duplicate so the ring has
    // unique vertices (mirrors polylineChainPaths) and the offset/seam softening
    // close cleanly.
    if (closed && centerline.length > 1) centerline.pop();
    const isChain = positions.length > 2;
    const radius =
        (isChain || routing !== "straight") ? (preset.cornerRadius ?? 0) : 0;
    const cornerStyle = preset.cornerStyle ?? "round";
    const soften = (pts: Vec2[]): Vec2[] => {
        if (closed) {
            return radius > 0
                ? closedPolygonToPoints(pts, radius, cornerStyle)
                : [...pts, pts[0]!];
        }
        return radius > 0 ? polylineToPoints(pts, radius, cornerStyle) : pts;
    };
    if (count === 1 || spacing === 0) return [soften(centerline)];
    const lines: Vec2[][] = [];
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        lines.push(soften(offsetPolyline(centerline, offset, closed)));
    }
    return lines;
}

/** A single populated object instance: where to place it and how to turn it. */
export interface ConnectionObjectPlacement {
    x: number;
    y: number;
    /** Tangent angle of the path at this point, in radians. */
    angle: number;
    /** Index into the preset's `objectSvgs`, so instances alternate. */
    objectIndex: number;
}

/**
 * Deterministic object index for a `random`-ordered instance. `mulberry32`-style
 * mix of the per-chain `seed` and the instance `position` keeps the choice
 * stable yet visually unordered, so the run doesn't repeat the same sequence.
 */
export function randomObjectIndex(
    seed: number,
    position: number,
    objectCount: number,
): number {
    let t = (seed + Math.imul(position + 1, 0x6d2b79f5)) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    return Math.min(objectCount - 1, Math.floor(r * objectCount));
}

/**
 * Walk one polyline by arc-length, emitting a placement every `spacing` units.
 * The first instance sits half a step in from the start so the run is centered
 * and endpoints aren't crowded; each placement's `angle` is the local tangent.
 * For `sequence` order `objectIndex` cycles so multiple objects alternate; for
 * `random` it's a seeded shuffle. `startIndex` lets a multi-line connection keep
 * the alternation/sequence continuous across its parallel lines.
 */
function samplePolylineByLength(
    points: Vec2[],
    spacing: number,
    objectCount: number,
    startIndex: number,
    order: ConnectionObjectOrder,
    seed: number,
): ConnectionObjectPlacement[] {
    if (points.length < 2 || spacing <= 0 || objectCount <= 0) return [];
    // Cumulative length and per-segment unit tangents.
    let total = 0;
    const segLen: number[] = [];
    const segAng: number[] = [];
    for (let i = 0; i < points.length - 1; i += 1) {
        const dx = points[i + 1]!.x - points[i]!.x;
        const dy = points[i + 1]!.y - points[i]!.y;
        const len = Math.hypot(dx, dy);
        segLen.push(len);
        segAng.push(Math.atan2(dy, dx));
        total += len;
    }
    if (total < 1e-6) return [];
    const out: ConnectionObjectPlacement[] = [];
    let counter = startIndex;
    // Center the run: leave half a leftover gap at each end.
    const n = Math.floor(total / spacing);
    const startOffset = (total - n * spacing) / 2;
    for (let dist = startOffset; dist <= total + 1e-6; dist += spacing) {
        // Locate `dist` along the segments.
        let acc = 0;
        let seg = 0;
        while (seg < segLen.length - 1 && acc + segLen[seg]! < dist) {
            acc += segLen[seg]!;
            seg += 1;
        }
        const segFrac = segLen[seg]! > 0 ? (dist - acc) / segLen[seg]! : 0;
        const a = points[seg]!;
        const b = points[seg + 1]!;
        const objectIndex =
            order === "random"
                ? randomObjectIndex(seed, counter, objectCount)
                : ((counter % objectCount) + objectCount) % objectCount;
        out.push({
            x: a.x + (b.x - a.x) * segFrac,
            y: a.y + (b.y - a.y) * segFrac,
            angle: segAng[seg]!,
            objectIndex,
        });
        counter += 1;
    }
    return out;
}

/**
 * Object placements for a whole connection chain (across every parallel line).
 * Returns [] when the preset carries no objects or has zero spacing. Object
 * alternation continues across lines so a multi-line connection doesn't reset
 * to the first object on each line.
 */
export function connectionObjectPlacements(
    chain: ConnectionChain,
): ConnectionObjectPlacement[] {
    const { preset } = chain;
    const objectCount = preset.objectSvgs?.length ?? 0;
    const spacing = preset.objectSpacing ?? 0;
    if (objectCount === 0 || spacing <= 0) return [];
    const order = preset.objectOrder ?? "sequence";
    // Seed off the chain's connection ids so each connection shuffles
    // differently (avoids every connection showing the same "random" pattern)
    // while staying stable across re-renders.
    const seed =
        order === "random" ? hashString(chain.connectionIds.join("|")) : 0;
    const polylines = connectionLinePolylines(chain);
    const out: ConnectionObjectPlacement[] = [];
    let startIndex = 0;
    for (const line of polylines) {
        const placements = samplePolylineByLength(
            line,
            spacing,
            objectCount,
            startIndex,
            order,
            seed,
        );
        out.push(...placements);
        startIndex += placements.length;
    }
    return out;
}

// --- preset-styled shape outlines -------------------------------------------
//
// A standalone shape (ellipse/rect/polygon/line/arc) can borrow a connection
// preset's *stroke* — its color/width/dash, parallel lines, and objects along
// the path — without borrowing its routing: the "path" is the shape's own
// outline rather than a route between two nodes. These helpers feed a shape's
// outline polyline (open for line/arc, closed for ellipse/rect/polygon) through
// the same parallel-offset + object-population machinery connections use, so the
// look matches exactly. The corner radius/style used to soften offset corners is
// the preset's own (the same one its bent routings use).

/** Number of samples used to flatten one closed shape outline (ellipse) ring. */
const SHAPE_OUTLINE_FLATTEN_STEPS = 96;

/**
 * Parallel-line polylines for a shape outline styled by a preset: the outline is
 * offset perpendicular `preset.lineCount` times (centered, `lineSpacing` apart)
 * and each offset line is softened by the preset's corner radius/style — exactly
 * how `connectionLinePolylines` builds a connection's parallel lines, but from a
 * caller-supplied outline instead of a routed centerline. `closed` rings are
 * offset and softened as loops (seam included). Returns one polyline per line.
 */
export function presetStrokePolylines(
    outline: Vec2[],
    closed: boolean,
    style: Pick<StrokeStyle, "lineCount" | "lineSpacing">,
): Vec2[][] {
    if (outline.length < 2) return [];
    const count = Math.max(1, Math.floor(style.lineCount ?? 1));
    const spacing = style.lineSpacing ?? 0;
    // A stroke style has no routing, so its offset parallel lines aren't
    // filleted; corners stay sharp (the shape's own corner radius softens its
    // plain outline). A closed ring just repeats its first point to close.
    const soften = (pts: Vec2[]): Vec2[] =>
        closed ? [...pts, pts[0]!] : pts;
    if (count === 1 || spacing === 0) return [soften(outline)];
    const lines: Vec2[][] = [];
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        lines.push(soften(offsetPolyline(outline, offset, closed)));
    }
    return lines;
}

/**
 * SVG `d` strings (one per parallel line) for a preset-styled shape outline.
 * Builds the same softened ring/polyline `presetStrokePolylines` does, but
 * serialized to paths — so the stroke layer of a preset-styled shape draws
 * identically to a connection's. Closed outlines emit closed (`Z`) paths.
 */
export function presetStrokeShapePaths(
    outline: Vec2[],
    closed: boolean,
    style: Pick<StrokeStyle, "lineCount" | "lineSpacing">,
): string[] {
    if (outline.length < 2) return [];
    const count = Math.max(1, Math.floor(style.lineCount ?? 1));
    const spacing = style.lineSpacing ?? 0;
    // No routing on a stroke style -> sharp corners on the offset lines.
    const toPath = (pts: Vec2[]): string =>
        closed
            ? closedPolygonToPath(pts, 0, "round")
            : polylineToPath(pts, 0, "round");
    if (count === 1 || spacing === 0) return [toPath(outline)];
    const paths: string[] = [];
    for (let i = 0; i < count; i += 1) {
        const offset = (i - (count - 1) / 2) * spacing;
        paths.push(toPath(offsetPolyline(outline, offset, closed)));
    }
    return paths;
}

/**
 * Object placements along a preset-styled shape outline (across every parallel
 * line), mirroring `connectionObjectPlacements`. `seedKey` seeds the `random`
 * object order so each shape shuffles distinctly yet stably (pass the shape id).
 */
export function presetStrokeObjectPlacements(
    outline: Vec2[],
    closed: boolean,
    style: Pick<
        StrokeStyle,
        | "lineCount"
        | "lineSpacing"
        | "objectSvgs"
        | "objectSpacing"
        | "objectOrder"
    >,
    seedKey: string,
): ConnectionObjectPlacement[] {
    const objectCount = style.objectSvgs?.length ?? 0;
    const spacing = style.objectSpacing ?? 0;
    if (objectCount === 0 || spacing <= 0) return [];
    const order = style.objectOrder ?? "sequence";
    const seed = order === "random" ? hashString(seedKey) : 0;
    const polylines = presetStrokePolylines(outline, closed, style);
    const out: ConnectionObjectPlacement[] = [];
    let startIndex = 0;
    for (const line of polylines) {
        const placements = samplePolylineByLength(
            line,
            spacing,
            objectCount,
            startIndex,
            order,
            seed,
        );
        out.push(...placements);
        startIndex += placements.length;
    }
    return out;
}

/** A shape's outline as a polyline plus whether it's a closed ring. */
export interface ShapeOutline {
    points: Vec2[];
    closed: boolean;
}

/**
 * Flatten an ellipse outline (honoring its partial-arc carve) into a polyline.
 * A full-sweep ellipse becomes a closed ring; a partial sweep an open arc. The
 * stored center/radii are axis-aligned; the shape's body `rotation` is applied
 * here so the preset stroke and objects ride the rotated outline directly
 * (callers emit them without the SVG rotate transform plain ellipses use).
 */
export function ellipseOutline(
    e: Pick<
        DiagramEllipse,
        "center" | "rx" | "ry" | "startAngle" | "sweep" | "rotation"
    >,
): ShapeOutline {
    if (e.rx <= 0 || e.ry <= 0) return { points: [], closed: false };
    const sweep = Math.max(0, Math.min(1, e.sweep));
    if (sweep <= 0) return { points: [], closed: false };
    const closed = sweep >= 1 - 1e-6;
    const sweepDeg = closed ? 360 : sweep * 360;
    // One sample per ~ (360 / STEPS) degrees, scaled to the swept fraction; a
    // closed ring drops the duplicate end sample so offset/softening close clean.
    const steps = Math.max(
        8,
        Math.round(SHAPE_OUTLINE_FLATTEN_STEPS * (sweepDeg / 360)),
    );
    const rot = ((e.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const points: Vec2[] = [];
    const last = closed ? steps - 1 : steps;
    for (let i = 0; i <= last; i += 1) {
        const deg = e.startAngle + (sweepDeg * i) / steps;
        const a = (deg * Math.PI) / 180;
        // Axis-aligned point, then spun about the center by the body rotation.
        const px = e.rx * Math.cos(a);
        const py = e.ry * Math.sin(a);
        points.push({
            x: e.center.x + px * cos - py * sin,
            y: e.center.y + px * sin + py * cos,
        });
    }
    return { points, closed };
}

/**
 * Outline polyline for a rect's preset stroke: its four corners as a closed
 * ring, spun by the box's body `rotation` so the preset stroke and objects ride
 * the rotated outline (callers emit them without the SVG rotate transform plain
 * rects use). Corner softening is left to the preset (its corner radius/style),
 * mirroring how a connection's bends are softened — the rect's own corner radius
 * governs only its plain stroke.
 */
export function rectOutline(
    rect: Pick<DiagramRect, "start" | "end" | "rotation">,
): ShapeOutline {
    const w = Math.abs(rect.start.x - rect.end.x);
    const h = Math.abs(rect.start.y - rect.end.y);
    if (w < 1e-6 || h < 1e-6) return { points: [], closed: true };
    const corners = rectCorners(rect);
    const center = rectCenter(rect);
    const rot = ((rect.rotation ?? 0) * Math.PI) / 180;
    if (Math.abs(rot) < 1e-6) return { points: corners, closed: true };
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const points = corners.map((p) => {
        const dx = p.x - center.x;
        const dy = p.y - center.y;
        return {
            x: center.x + dx * cos - dy * sin,
            y: center.y + dx * sin + dy * cos,
        };
    });
    return { points, closed: true };
}

/**
 * Outline polyline for a polygon's preset stroke: its vertices as a closed ring.
 * Corner softening is the preset's job (mirrors `rectOutline`); the polygon's
 * own corner radius governs only its plain stroke.
 */
export function polygonOutline(
    polygon: Pick<DiagramPolygon, "points">,
): ShapeOutline {
    if (polygon.points.length < 2) return { points: [], closed: true };
    return { points: polygon.points.map((p) => ({ ...p })), closed: true };
}

/** Outline polyline for a line's preset stroke: its two endpoints (open). */
export function lineOutline(
    line: Pick<DiagramLine, "start" | "end">,
): ShapeOutline {
    if (line.start.x === line.end.x && line.start.y === line.end.y) {
        return { points: [], closed: false };
    }
    return { points: [{ ...line.start }, { ...line.end }], closed: false };
}

/**
 * Outline polyline for an arc's preset stroke: the arc flattened into points
 * (same bulge math as the drawn stroke), open. Reuses `arcToPoints` so the
 * preset stroke/objects ride exactly the curve `diagramArcPath` draws.
 */
export function arcOutline(
    arc: Pick<DiagramArc, "start" | "end" | "flip" | "sweep">,
): ShapeOutline {
    if (arc.start.x === arc.end.x && arc.start.y === arc.end.y) {
        return { points: [], closed: false };
    }
    return {
        points: arcToPoints(arc.start, arc.end, arc.flip, arc.sweep),
        closed: false,
    };
}

// --- connection object SVG (shared parse + transform) ------------------------

/** Parsed object SVG: inner markup, viewBox, and size for placement scaling. */
export interface ParsedConnectionObject {
    /** Markup inside the outer `<svg>` (everything that should be cloned). */
    inner: string;
    /** viewBox dimensions used to size/scale the object. */
    width: number;
    height: number;
}

/**
 * Pull the inner markup and viewBox size out of an object SVG string. Returns
 * null when the string isn't usable (no `<svg>` body or no positive viewBox).
 */
export function parseConnectionObject(
    svg: string,
): ParsedConnectionObject | null {
    const innerMatch = svg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
    if (!innerMatch) return null;
    const inner = innerMatch[1] ?? "";
    const vbMatch = svg.match(/viewBox=["']([^"']+)["']/i);
    let width = 100;
    let height = 100;
    if (vbMatch) {
        const parts = vbMatch[1]!.split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts[2]! > 0 && parts[3]! > 0) {
            width = parts[2]!;
            height = parts[3]!;
        }
    }
    return { inner, width, height };
}

/**
 * Override the `fill` of every shape element in an object's inner markup with
 * `color`. Mirrors Object-along-Path's recolor: existing fills are stripped and
 * replaced, and shapes with no fill gain one, so a single color tints the whole
 * object. An empty/undefined color leaves the markup untouched.
 */
export function recolorConnectionObjectInner(
    inner: string,
    color: string | null | undefined,
): string {
    if (!inner || !color || !color.trim()) return inner;
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return inner.replace(
        elementRegex,
        (_match, tagName: string, attributes: string, selfClose?: string) => {
            const cleaned = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleaned} fill="${color}"${closing}`;
        },
    );
}

/**
 * Recolor one object instance's inner markup, honoring a heat-reactive style: if
 * `style.heatReactive` is on and any heat zone reaches the instance's `point`,
 * the object is tinted toward that zone's color (blended from the style's
 * `objectColor` base by proximity); otherwise the flat `objectColor` is used.
 * Shared by the viewer and exporter so both paint identically. When the style
 * isn't reactive (or no zones reach), this collapses to the plain recolor.
 */
export function recolorObjectForHeat(
    inner: string,
    style: Pick<StrokeStyle, "objectColor" | "heatReactive">,
    point: Vec2,
    zones: ReadonlyArray<DiagramHeatZone>,
): string {
    const base =
        style.objectColor && style.objectColor.trim() ? style.objectColor : "";
    // Not reactive (or no zones): fall back to the flat recolor (which leaves
    // the object's own colors when `objectColor` is unset).
    if (!style.heatReactive || zones.length === 0) {
        return recolorConnectionObjectInner(inner, style.objectColor);
    }
    const heat = heatAt(point, zones);
    if (!heat) return recolorConnectionObjectInner(inner, style.objectColor);
    // With an `objectColor` base, blend it toward the zone color by intensity.
    // Without one, the object keeps its own colors away from heat and is tinted
    // to the zone color where heat is present.
    const target = base ? lerpHexColor(base, heat.color, heat.intensity) : heat.color;
    if (!base && heat.intensity <= 0) return inner;
    return recolorConnectionObjectInner(inner, target);
}

/**
 * The transform string that places one object instance: translate to the point,
 * rotate (tangent angle for `align`, plus the preset's angle offset; the angle
 * outright for `fixed`), scale the object's viewBox down to `objectScale`, and
 * recenter so the object's middle sits on the path point.
 */
export function connectionObjectTransform(
    placement: ConnectionObjectPlacement,
    parsed: ParsedConnectionObject,
    preset: Pick<
        StrokeStyle,
        "objectScale" | "objectRotation" | "objectAngleDeg"
    >,
): string {
    const maxDim = Math.max(parsed.width, parsed.height) || 1;
    const scale = (preset.objectScale ?? 16) / maxDim;
    const tangentDeg = (placement.angle * 180) / Math.PI;
    const angleDeg =
        (preset.objectRotation ?? "align") === "align"
            ? tangentDeg + (preset.objectAngleDeg ?? 0)
            : (preset.objectAngleDeg ?? 0);
    // Center the (scaled) object on the path point.
    const halfW = (parsed.width * scale) / 2;
    const halfH = (parsed.height * scale) / 2;
    return (
        `translate(${round2(placement.x)} ${round2(placement.y)}) ` +
        `rotate(${round2(angleDeg)}) ` +
        `translate(${round2(-halfW)} ${round2(-halfH)}) ` +
        `scale(${round2(scale)})`
    );
}

/** Monotone-chain convex hull of 2D points (CCW), used for group outlines. */
function convexHull2d(points: Vec2[]): Vec2[] {
    if (points.length < 3) return [...points];
    const sorted = [...points].sort((a, b) =>
        a.x === b.x ? a.y - b.y : a.x - b.x,
    );
    const cross = (o: Vec2, a: Vec2, b: Vec2) =>
        (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower: Vec2[] = [];
    for (const p of sorted) {
        while (
            lower.length >= 2 &&
            cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0
        ) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper: Vec2[] = [];
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
        const p = sorted[i]!;
        while (
            upper.length >= 2 &&
            cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0
        ) {
            upper.pop();
        }
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/**
 * Expand a convex polygon outward by a uniform perpendicular distance `pad`.
 *
 * Each edge is offset along its own outward normal by `pad`, then consecutive
 * offset edges are intersected to find the new vertices. This keeps every edge
 * exactly `pad` away from the original — unlike a radial push from the centroid,
 * which leaves sharp corners closer to the shape than flat ones.
 *
 * Falls back to a radial push for degenerate inputs (fewer than 3 points, e.g.
 * an open polyline) where edge offsetting is not well defined.
 */
function padPolygon(poly: Vec2[], pad: number): Vec2[] {
    if (poly.length === 0 || pad === 0) return poly;
    if (poly.length < 3) return radialPad(poly, pad);

    const n = poly.length;
    const cx = poly.reduce((s, p) => s + p.x, 0) / n;
    const cy = poly.reduce((s, p) => s + p.y, 0) / n;

    // For each edge a->b, offset it outward by `pad`. The outward normal is the
    // edge direction rotated 90 degrees, with the sign chosen so it points away
    // from the centroid — robust to winding order and screen-space y-down.
    const offsetEdges = poly.map((a, i) => {
        const b = poly[(i + 1) % n]!;
        const ex = b.x - a.x;
        const ey = b.y - a.y;
        const len = Math.hypot(ex, ey) || 1;
        let nx = ey / len;
        let ny = -ex / len;
        // Flip the normal if it points toward the centroid.
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        if (nx * (mx - cx) + ny * (my - cy) < 0) {
            nx = -nx;
            ny = -ny;
        }
        return {
            px: a.x + nx * pad,
            py: a.y + ny * pad,
            dx: ex,
            dy: ey,
            nx,
            ny,
        };
    });

    // Each new vertex is the intersection of the offset of the previous edge
    // and the offset of the current edge.
    const out: Vec2[] = [];
    for (let i = 0; i < n; i += 1) {
        const e1 = offsetEdges[(i - 1 + n) % n]!;
        const e2 = offsetEdges[i]!;
        const det = e1.dx * e2.dy - e1.dy * e2.dx;
        if (Math.abs(det) < 1e-9) {
            // Parallel edges (collinear corner): just push the vertex along the
            // current edge's outward normal.
            out.push({ x: poly[i]!.x + e2.nx * pad, y: poly[i]!.y + e2.ny * pad });
            continue;
        }
        const t = ((e2.px - e1.px) * e2.dy - (e2.py - e1.py) * e2.dx) / det;
        out.push({ x: e1.px + e1.dx * t, y: e1.py + e1.dy * t });
    }
    return out;
}

/** Push each point radially away from the polygon centroid by `pad` units. */
function radialPad(poly: Vec2[], pad: number): Vec2[] {
    const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
    const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
    return poly.map((p) => {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const len = Math.hypot(dx, dy) || 1;
        return { x: p.x + (dx / len) * pad, y: p.y + (dy / len) * pad };
    });
}

/**
 * Convex enclosure of `points` whose edges may only point in one of `bends`
 * evenly spaced directions (so the result is a 2*bends-gon). Built with the
 * support-function method: for each allowed edge, take the supporting line of
 * the point set, push it outward by `pad`, then intersect consecutive lines.
 *
 * bends = 2 yields the axis-aligned bounding box; bends = 4 an octagon; higher
 * values hug the point cloud progressively tighter. Returned clockwise in
 * screen space (y-down) starting from the top edge, matching a plain box.
 */
function directionalHull(points: Vec2[], bends: number, pad: number): Vec2[] {
    const k = Math.max(2, Math.floor(bends));
    const edges = k * 2; // a 2k-gon
    // Outward edge normals, evenly spaced. Start at straight up (0,-1) so the
    // first edge is the top one; step clockwise in screen space (y-down).
    const normals: Vec2[] = [];
    for (let i = 0; i < edges; i += 1) {
        const ang = -Math.PI / 2 + (i / edges) * Math.PI * 2;
        normals.push({ x: Math.cos(ang), y: Math.sin(ang) });
    }
    // Support distance of the (padded) cloud along each normal.
    const support = normals.map((n) => {
        let h = -Infinity;
        for (const p of points) {
            const d = p.x * n.x + p.y * n.y;
            if (d > h) h = d;
        }
        return h + pad;
    });
    // Each edge i lies on the line n_i . x = support_i. Vertices are the
    // intersections of consecutive edge lines.
    const out: Vec2[] = [];
    for (let i = 0; i < edges; i += 1) {
        const n1 = normals[i]!;
        const n2 = normals[(i + 1) % edges]!;
        const h1 = support[i]!;
        const h2 = support[(i + 1) % edges]!;
        const det = n1.x * n2.y - n1.y * n2.x;
        if (Math.abs(det) < 1e-9) continue; // parallel (shouldn't happen)
        out.push({
            x: (h1 * n2.y - h2 * n1.y) / det,
            y: (n1.x * h2 - n2.x * h1) / det,
        });
    }
    return out;
}

/**
 * Replace each edge of a convex polygon (CCW or CW) with a staircase whose legs
 * are locked to the allowed `directions` (unit vectors), so the wrap stays
 * tight to the polygon while every edge runs along an allowed direction. Edges
 * already aligned to an allowed direction are kept as-is.
 *
 * For each edge A->B the two allowed directions that bracket the edge's heading
 * are used. `bends` controls how many steps subdivide the edge: 1 inserts a
 * single corner (one L), higher values zig-zag between the two directions in
 * `bends` even steps, hugging the original edge more closely.
 */
function steppedPolygon(
    poly: Vec2[],
    directions: Vec2[],
    bends: number,
): Vec2[] {
    const n = poly.length;
    if (n < 2 || directions.length < 2) return poly;
    const steps = Math.max(1, Math.floor(bends));

    const out: Vec2[] = [];
    for (let i = 0; i < n; i += 1) {
        const a = poly[i]!;
        const b = poly[(i + 1) % n]!;
        const ex = b.x - a.x;
        const ey = b.y - a.y;
        const elen = Math.hypot(ex, ey);
        out.push(a);
        if (elen < 1e-9) continue;
        const ux = ex / elen;
        const uy = ey / elen;

        // Bracket the edge direction with two allowed directions:
        //   lo = best-aligned direction clockwise of the edge (cross <= 0),
        //   hi = best-aligned direction counter-clockwise of it (cross >= 0).
        // "best-aligned" = largest dot product with the edge direction.
        let lo: Vec2 | null = null;
        let hi: Vec2 | null = null;
        let loBest = -Infinity;
        let hiBest = -Infinity;
        let aligned = false;
        for (const d of directions) {
            const dot = d.x * ux + d.y * uy;
            const cross = ux * d.y - uy * d.x; // > 0: d is CCW of the edge
            if (dot > 1 - 1e-9) {
                aligned = true; // edge already runs along an allowed direction
                break;
            }
            if (cross <= 1e-9 && dot > loBest) {
                loBest = dot;
                lo = d;
            }
            if (cross >= -1e-9 && dot > hiBest) {
                hiBest = dot;
                hi = d;
            }
        }
        if (aligned || !lo || !hi) continue;

        // Decompose the edge as e = alpha*lo + beta*hi (both >= 0 since lo/hi
        // bracket the edge direction). Then walk `steps` zig-zags, each doing
        // alpha/steps along lo, then beta/steps along hi. The last hi-leg lands
        // exactly on B, so it's omitted (B is appended by the next iteration).
        const det = lo.x * hi.y - lo.y * hi.x;
        if (Math.abs(det) < 1e-9) continue;
        const alpha = (ex * hi.y - ey * hi.x) / det;
        const beta = (lo.x * ey - lo.y * ex) / det;
        const la = alpha / steps;
        const lb = beta / steps;
        let cur = { ...a };
        for (let s = 0; s < steps; s += 1) {
            cur = { x: cur.x + lo.x * la, y: cur.y + lo.y * la };
            out.push(cur);
            // Skip the final hi-leg endpoint (it is B).
            if (s < steps - 1) {
                cur = { x: cur.x + hi.x * lb, y: cur.y + hi.y * lb };
                out.push(cur);
            }
        }
    }
    return out;
}

/** Unit direction vectors evenly spaced over 360 degrees, `count` of them. */
function evenDirections(count: number): Vec2[] {
    const dirs: Vec2[] = [];
    for (let i = 0; i < count; i += 1) {
        const ang = (i / count) * Math.PI * 2;
        dirs.push({ x: Math.cos(ang), y: Math.sin(ang) });
    }
    return dirs;
}

/**
 * Outline polygon points for a group, in canvas coordinates. `hull` is the
 * padded convex hull; `angular` is a directional hull constrained to
 * `bendCount` edge directions; `ortho`/`diag` are tight stepped hulls whose
 * edges are locked to 90-degree / 45-degree directions. (An open, non-closed
 * outline only applies to the hull shape.)
 */
export function groupOutlinePoints(
    positions: Vec2[],
    outline: OutlineProperty,
): Vec2[] {
    if (positions.length < 2) return [];
    const shape = outline.shape ?? "hull";
    if (shape === "angular") {
        return directionalHull(
            positions,
            outline.bendCount ?? 4,
            outline.padding,
        );
    }
    if (shape === "ortho" || shape === "diag") {
        // Tight convex hull, padded, with every edge stepped onto the allowed
        // directions (4 for 90-degree, 8 for 45-degree). `bendCount` subdivides
        // each edge into that many staircase steps.
        const padded = padPolygon(convexHull2d(positions), outline.padding);
        const dirs = evenDirections(shape === "ortho" ? 4 : 8);
        return steppedPolygon(padded, dirs, outline.bendCount ?? 1);
    }
    const hull = outline.closed ? convexHull2d(positions) : positions;
    return padPolygon(hull, outline.padding);
}

/**
 * SVG path `d` for a group outline, with corners filleted by `cornerRadius`.
 * The angular/ortho/diag enclosures are always closed; `hull` honors the
 * `closed` flag. Returns null when there aren't enough nodes to form a shape.
 */
export function groupOutlinePath(
    positions: Vec2[],
    outline: OutlineProperty,
): string | null {
    const pts = groupOutlinePoints(positions, outline);
    if (pts.length < 2) return null;
    const radius = outline.cornerRadius ?? 0;
    const corner = outline.cornerStyle ?? "round";
    const shape = outline.shape ?? "hull";
    // The enclosing shapes are inherently closed; a hull only closes when
    // flagged.
    const closed = shape !== "hull" || outline.closed;
    return closed
        ? closedPolygonToPath(pts, radius, corner)
        : polylineToPath(pts, radius, corner);
}

// --- node shapes ------------------------------------------------------------

/**
 * Resolved geometry for a node marker, consumable by both the string-based
 * SVG export and the JSX viewer. `r` is the node's half-extent (radius).
 */
export type NodeShapeGeometry =
    | { kind: "circle"; cx: number; cy: number; r: number }
    | { kind: "rect"; x: number; y: number; width: number; height: number }
    | { kind: "polygon"; points: Array<[number, number]> };

export function nodeShapeGeometry(
    center: Vec2,
    r: number,
    shape: NodeShape,
): NodeShapeGeometry {
    const cx = round2(center.x);
    const cy = round2(center.y);
    const rr = round2(r);
    switch (shape) {
        case "square":
            return {
                kind: "rect",
                x: round2(cx - rr),
                y: round2(cy - rr),
                width: round2(rr * 2),
                height: round2(rr * 2),
            };
        case "diamond":
            return {
                kind: "polygon",
                points: [
                    [cx, round2(cy - rr)],
                    [round2(cx + rr), cy],
                    [cx, round2(cy + rr)],
                    [round2(cx - rr), cy],
                ],
            };
        case "triangle": {
            // Equilateral-ish, pointing up, centered on the node.
            const h = round2(rr * 1.5);
            return {
                kind: "polygon",
                points: [
                    [cx, round2(cy - h)],
                    [round2(cx + rr), round2(cy + h * 0.5)],
                    [round2(cx - rr), round2(cy + h * 0.5)],
                ],
            };
        }
        case "circle":
        default:
            return { kind: "circle", cx, cy, r: rr };
    }
}

/** Serialize a node-shape geometry to an SVG element string (for export). */
export function nodeShapeSvg(geom: NodeShapeGeometry, fill: string): string {
    switch (geom.kind) {
        case "rect":
            return `<rect x="${geom.x}" y="${geom.y}" width="${geom.width}" height="${geom.height}" fill="${fill}" />`;
        case "polygon": {
            const pts = geom.points.map(([x, y]) => `${x},${y}`).join(" ");
            return `<polygon points="${pts}" fill="${fill}" />`;
        }
        case "circle":
        default:
            return `<circle cx="${geom.cx}" cy="${geom.cy}" r="${geom.r}" fill="${fill}" />`;
    }
}

// --- ellipse geometry -------------------------------------------------------

/** Point on an ellipse at parametric angle `deg` (degrees, clockwise from +X). */
function ellipsePoint(e: Pick<DiagramEllipse, "center" | "rx" | "ry">, deg: number): Vec2 {
    const a = (deg * Math.PI) / 180;
    return {
        x: e.center.x + e.rx * Math.cos(a),
        y: e.center.y + e.ry * Math.sin(a),
    };
}

/**
 * SVG path `d` for an ellipse outline. A full-sweep ellipse is a closed ring;
 * a partial sweep is an open arc from `startAngle` spanning `sweep`×360°,
 * drawn as a stroke (it is never closed back to the center or across the
 * chord). Returns "" when the ellipse is degenerate (zero radius).
 */
export function ellipsePath(
    e: Pick<DiagramEllipse, "center" | "rx" | "ry" | "startAngle" | "sweep">,
): string {
    if (e.rx <= 0 || e.ry <= 0) return "";
    const sweep = Math.max(0, Math.min(1, e.sweep));
    if (sweep <= 0) return "";
    if (sweep >= 1 - 1e-6) {
        // Full ellipse: two half-arcs close the ring (a single A can't draw a
        // 360° arc since the endpoints would coincide).
        const left = ellipsePoint(e, e.startAngle);
        const right = ellipsePoint(e, e.startAngle + 180);
        const rx = round2(e.rx);
        const ry = round2(e.ry);
        return (
            `M${round2(left.x)} ${round2(left.y)} ` +
            `A${rx} ${ry} 0 0 1 ${round2(right.x)} ${round2(right.y)} ` +
            `A${rx} ${ry} 0 0 1 ${round2(left.x)} ${round2(left.y)} Z`
        );
    }
    const sweepDeg = sweep * 360;
    const start = ellipsePoint(e, e.startAngle);
    const end = ellipsePoint(e, e.startAngle + sweepDeg);
    const largeArc = sweepDeg > 180 ? 1 : 0;
    return (
        `M${round2(start.x)} ${round2(start.y)} ` +
        `A${round2(e.rx)} ${round2(e.ry)} 0 ${largeArc} 1 ` +
        `${round2(end.x)} ${round2(end.y)}`
    );
}

// --- line & arc geometry ----------------------------------------------------

/** SVG `d` for a straight line between two points. "" when degenerate. */
export function linePath(line: Pick<DiagramLine, "start" | "end">): string {
    const { start, end } = line;
    if (start.x === end.x && start.y === end.y) return "";
    return (
        `M${round2(start.x)} ${round2(start.y)} ` +
        `L${round2(end.x)} ${round2(end.y)}`
    );
}

/**
 * SVG `d` for a standalone arc: a circular arc that shares the two endpoints
 * but bulges to one side, sized by `sweep` the same way connection `arc`
 * routing is. Reuses `arcPath` so the bulge math stays in one place. Returns ""
 * when the endpoints coincide.
 */
export function diagramArcPath(
    arc: Pick<DiagramArc, "start" | "end" | "flip" | "sweep">,
): string {
    if (arc.start.x === arc.end.x && arc.start.y === arc.end.y) return "";
    return arcPath(arc.start, arc.end, arc.flip, arc.sweep);
}

/** The four corners (TL, TR, BR, BL) of a rect's two-point bounding box. */
export function rectCorners(
    rect: Pick<DiagramRect, "start" | "end">,
): [Vec2, Vec2, Vec2, Vec2] {
    const minX = Math.min(rect.start.x, rect.end.x);
    const maxX = Math.max(rect.start.x, rect.end.x);
    const minY = Math.min(rect.start.y, rect.end.y);
    const maxY = Math.max(rect.start.y, rect.end.y);
    return [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY },
    ];
}

/**
 * SVG `d` for a rectangle from its two-point bounding box. Corners are softened
 * (rounded arc or beveled chamfer) by `cornerRadius`/`cornerStyle`, reusing the
 * shared closed-polygon serializer. Returns "" when the box has no area.
 */
export function rectPath(
    rect: Pick<DiagramRect, "start" | "end" | "cornerRadius" | "cornerStyle">,
): string {
    const corners = rectCorners(rect);
    const w = Math.abs(rect.start.x - rect.end.x);
    const h = Math.abs(rect.start.y - rect.end.y);
    if (w < 1e-6 || h < 1e-6) return "";
    // Clamp the fillet to half the shorter side so it never self-overlaps.
    const radius = Math.max(0, Math.min(rect.cornerRadius, w / 2, h / 2));
    return closedPolygonToPath(corners, radius, rect.cornerStyle);
}

/** The center of a rect's two-point bounding box. */
export function rectCenter(rect: Pick<DiagramRect, "start" | "end">): Vec2 {
    return {
        x: (rect.start.x + rect.end.x) / 2,
        y: (rect.start.y + rect.end.y) / 2,
    };
}

/**
 * An SVG `transform="rotate(θ cx cy)"` attribute spinning a shape about `center`
 * by `rotationDeg` degrees clockwise — or "" when the rotation is negligible, so
 * unrotated shapes emit no transform. Shared by the ellipse and rect renderers
 * (canvas + export) and the partial-arc / fill geometry, which all stay built
 * axis-aligned and are simply spun by this wrapper.
 */
export function rotateTransformAttr(
    center: Vec2,
    rotationDeg: number,
): string {
    if (!rotationDeg || Math.abs(rotationDeg) < 1e-6) return "";
    return ` transform="rotate(${round2(rotationDeg)} ${round2(center.x)} ${round2(center.y)})"`;
}

// --- polygon geometry -------------------------------------------------------

/**
 * SVG `d` for a closed polygon from its ordered vertices. Corners are softened
 * (rounded arc or beveled chamfer) by `cornerRadius`/`cornerStyle`, reusing the
 * shared closed-polygon serializer. Returns "" with fewer than two vertices
 * (nothing to draw); two vertices render as a closed back-and-forth segment.
 */
export function polygonPath(
    polygon: Pick<DiagramPolygon, "points" | "cornerRadius" | "cornerStyle">,
): string {
    if (polygon.points.length < 2) return "";
    const radius = Math.max(0, polygon.cornerRadius);
    return closedPolygonToPath(polygon.points, radius, polygon.cornerStyle);
}

